import { getImageInfo, downloadFile, config } from '@/utils/common';
/**
 * Canvas工具类
 * 提供Canvas初始化和图片创建相关的功能
 * 包含图片加载、缓存管理、Canvas绘制等核心工具
 */

const { Cache } = require('./Cache.js');
import { cache } from './Cache.js';
import { whiteInkFilterAlpha } from './constant.js';
import { logger } from './logger.js';

// fillNonTransparentPixels 函数专用的缓存实例，最大容量为 20
const fillNonTransparentPixelsCache = new Cache(10);

// fillBlackPixelsWithHexColor 函数专用的缓存实例，最大容量为 10
const fillBlackPixelsWithHexColorCache = new Cache(10);

// fillBlackPixelsWithHexColorByComposite 函数专用的缓存实例，最大容量为 10
const fillBlackPixelsWithHexColorByCompositeCache = new Cache(10);

// convertToGrayImage 函数专用的缓存实例，最大容量为 20
const convertToGrayImageInstance = new Cache(10);

// convertGrayToAlphaForBackground 函数专用的缓存实例，最大容量为 10
const convertGrayToAlphaForBackgroundInstance = new Cache(10);

/**
 * 将HEX颜色转换为RGB数组
 * 支持格式：#RGB 或 #RRGGBB
 * @param {string} hexColor - 颜色值，如 "#fff"、"#ffffff"
 * @returns {number[]} 返回 [r, g, b]
 */
function parseHexColorToRgb(hexColor) {
  if (!hexColor || typeof hexColor !== 'string') {
    throw new Error('hexColor 不能为空，且必须为字符串');
  }

  let hex = hexColor.trim();
  if (hex[0] !== '#') {
    throw new Error('hexColor 格式错误，必须以 # 开头，例如：#fff 或 #ffffff');
  }

  if (hex.length !== 4 && hex.length !== 7) {
    throw new Error('hexColor 格式错误，仅支持 #RGB 或 #RRGGBB');
  }

  // #RGB → #RRGGBB
  if (hex.length === 4) {
    hex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  }

  const rStr = hex.slice(1, 3);
  const gStr = hex.slice(3, 5);
  const bStr = hex.slice(5, 7);

  const r = parseInt(rStr, 16);
  const g = parseInt(gStr, 16);
  const b = parseInt(bStr, 16);

  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    throw new Error('hexColor 格式错误，必须为有效的十六进制颜色值');
  }

  return [r, g, b];
}

/**
 * 图片创建器类
 *
 * 功能说明：
 * 管理图片加载和缓存，提高图片重复使用的性能。
 * 支持失败重试机制，确保图片加载的可靠性。
 *
 * 主要特性：
 * - 图片缓存：避免重复加载相同图片
 * - 跨域处理：自动设置CORS
 * - 失败重试：可配置重试次数和间隔
 * - 过期管理：支持设置缓存过期时间
 *
 * 使用场景：
 * 在图片编辑应用中，同一张图片可能被多次使用（预览、编辑、导出等），
 * 使用缓存可以显著提升性能和用户体验。
 */
export class ImageCreator {
  constructor(key) {
    this.key = key;
    this.createImageCache = new Cache();
  }

  /**
   * 清除图片缓存
   */
  clearCache() {
    this.createImageCache.clear();
  }

  /**
   * 获取缓存中的图片
   * @param {string} imageSrc - 图片源URL
   * @returns {Image|null} 缓存的图片对象或null
   */
  getCachedImage(imageSrc) {
    return this.createImageCache.get(imageSrc);
  }

  /**
   * 设置图片到缓存
   * @param {Object} params - 参数对象
   * @param {string} params.imageSrc - 图片源URL
   * @param {Image} params.image - 图片对象
   * @param {number} params.expirationTime - 过期时间（毫秒），可选
   */
  setCachedImage({ imageSrc, image, expirationTime }) {
    this.createImageCache.set(imageSrc, image, expirationTime);
  }

  /**
   * 创建图片对象（内部方法）
   * 加载图片并处理跨域问题，支持图片缓存
   * @param {string} imageSrc - 图片源URL
   * @param {number} count - 重试次数
   * @returns {Promise<Image>} 图片对象
   */
  _createImage(imageSrc, count) {
    return new Promise(async (resolve, reject) => {
      if (!imageSrc) {
        reject(new Error('图片源不能为空'));
        return;
      }

      const img = new Image();

      // 处理跨域问题
      if (/^http/.test(imageSrc) && !this.isDev()) {
        img.crossOrigin = 'anonymous';
      }

      // 检查缓存
      let cachedImage = this.getCachedImage(imageSrc);
      if (cachedImage) {
        resolve(cachedImage);
        return;
      }

      img.src = imageSrc;

      img.onload = () => {
        // 缓存图片，设置30分钟过期时间
        this.setCachedImage({
          imageSrc,
          image: img,
          expirationTime: 30 * 60 * 1000
        });
        resolve(img);
      };

      img.onerror = (err) => {
        console.error('createImage error', img.src);
        reject(err);
        // 可以在这里添加错误处理逻辑
      };
    });
  }

  /**
   * 创建图片对象（带重试机制）
   * 调用_createImage方法，并添加重试机制处理加载失败的情况
   * @param {Object|string} params - 参数对象或图片源URL（向后兼容）
   * @param {string} params.imageSrc - 图片源URL
   * @param {number} params.maxRetries - 最大重试次数，默认10
   * @param {number} params.retryInterval - 重试间隔，默认1000ms
   * @returns {Promise<Image>} 图片对象
   */
  createImage(params) {
    // 向后兼容：如果传入的是字符串，转换为对象形式
    if (typeof params === 'string') {
      params = { imageSrc: params };
    }

    if (!params.imageSrc) {
      throw new Error('图片源不能为空');
    }

    // 使用重试机制创建图片
    return this._createImageWithRetry(params);
  }

  /**
   * 带重试机制的图片创建
   * @param {Object} params - 参数对象
   * @param {string} params.imageSrc - 图片源URL
   * @param {number} params.maxRetries - 最大重试次数，默认10
   * @param {number} params.retryInterval - 重试间隔，默认1000ms
   * @returns {Promise<Image>} 图片对象
   */
  _createImageWithRetry({ imageSrc, maxRetries = 10, retryInterval = 1000 }) {
    return new Promise((resolve, reject) => {
      let retryCount = 0;

      const attempt = () => {
        this._createImage(imageSrc, retryCount)
          .then(resolve)
          .catch((error) => {
            retryCount++;
            if (retryCount <= maxRetries) {
              console.warn(`图片加载失败，第${retryCount}次重试:`, imageSrc);
              setTimeout(attempt, retryInterval);
            } else {
              console.error(`图片加载失败，已重试${maxRetries}次:`, imageSrc);
              reject(error);
            }
          });
      };

      attempt();
    });
  }

  /**
   * 判断是否为开发环境
   * @returns {boolean} 是否为开发环境
   */
  isDev() {
    return process.env.NODE_ENV === 'development';
  }
}

/**
 * 初始化Canvas
 *
 * 功能说明：
 * 创建或获取canvas元素，设置正确的尺寸和上下文。
 * 自动处理设备像素比（DPR），确保在高清屏幕上显示清晰。
 *
 * DPR处理逻辑：
 * 1. 获取设备像素比（如Retina屏为2）
 * 2. 将canvas实际像素设置为 逻辑尺寸 × DPR
 * 3. 缩放绘图上下文，使绘制API使用逻辑坐标
 *
 * 举例：
 * - 逻辑尺寸：400x300
 * - DPR=2的设备：canvas实际尺寸800x600，ctx.scale(2,2)
 * - 绘制时使用逻辑坐标drawImage(img, 0, 0, 100, 100)
 * - 实际绘制到200x200的物理像素区域
 *
 * 尺寸限制：
 * canvas尺寸不得超过4096px（小程序限制）
 *
 * @param {Object} params - 参数对象
 * @param {string} params.selector - canvas选择器（如'#myCanvas'）
 * @param {number} params.canvasWidth - canvas逻辑宽度
 * @param {number} params.canvasHeight - canvas逻辑高度
 * @param {HTMLCanvasElement} params.dom - canvas DOM元素（可选，优先使用）
 * @returns {Promise<Object>} 初始化结果
 * @returns {CanvasRenderingContext2D} returns.ctx - Canvas 2D上下文
 * @returns {HTMLCanvasElement} returns.canvasNode - Canvas DOM元素
 * @returns {number} returns.canvasWidth - Canvas逻辑宽度
 * @returns {number} returns.canvasHeight - Canvas逻辑高度
 */
export function initCanvas({
  selector,
  canvasWidth,
  canvasHeight,
  dom,
  customDpr
}) {
  return new Promise((resolve, reject) => {
    try {
      let selectedNode = null;
      if (selector && typeof selector === 'string') {
        selectedNode = document.querySelector(selector);
      }
      const canvasNode = selectedNode || dom;
      if (!canvasNode && !dom) {
        reject(new Error(`Canvas元素未找到: ${selector}`));
        return;
      }

      const ctx = canvasNode.getContext('2d');
      if (!ctx) {
        reject(new Error('无法获取Canvas 2D上下文'));
        return;
      }

      let dpr = customDpr || window.devicePixelRatio || 1;

      // 小程序限制不得宽高超过4096
      canvasNode.width = Math.floor(canvasWidth * dpr);
      canvasNode.height = Math.floor(canvasHeight * dpr);
      ctx.scale(dpr, dpr);

      resolve({
        ctx,
        canvasNode,
        canvasWidth,
        canvasHeight
      });
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * 创建ImageCreator实例的工厂函数
 * @param {string} key - 唯一标识符
 * @returns {ImageCreator} ImageCreator实例
 */
export function createImageCreator(key) {
  return new ImageCreator(key);
}

/**
 * 清除指定key的图片缓存
 * @param {string} key - 缓存标识符
 */
export function clearImageCache(key) {
  // 这里可以实现全局缓存管理逻辑
  console.log(`清除图片缓存: ${key}`);
}

/**
 * 加载图片（简单版本）
 *
 * 功能说明：
 * 加载图片并自动处理跨域问题。
 * 相比ImageCreator，这是一个更轻量级的图片加载工具，不包含缓存和重试功能。
 *
 * 使用场景：
 * - 一次性加载的图片
 * - 不需要缓存的临时图片
 * - 快速原型开发
 *
 * 跨域处理：
 * 设置crossOrigin='Anonymous'，允许读取跨域图片的像素数据。
 * 这对于需要getImageData()的场景（如白墨处理）是必需的。
 *
 * @param {string} src - 图片源URL（支持http(s)://、data:、blob:等）
 * @returns {Promise<HTMLImageElement>} 加载完成的图片对象
 */
export function loadImage(src) {
  return new Promise((resolve, reject) => {
    let img = new Image();
    img.setAttribute('crossOrigin', 'Anonymous');
    img.onload = () => {
      resolve(img);
    };
    img.onerror = (err) => {
      reject(err);
    };
    img.src = src;
  });
}

/**
 * 按目标比例补边导出图片
 *
 * 处理规则：
 * - 原图比例与目标比例一致时，按原尺寸导出
 * - 原图更窄时，扩展画布宽度
 * - 原图更宽时，扩展画布高度
 * - 始终保留原图完整内容，不裁切、不拉伸，短边居中
 *
 * @param {string|HTMLImageElement|HTMLCanvasElement} image - 图片地址或已加载的图片对象
 * @param {string|number} ratio - 目标比例，支持 "1:1"、"3:4" 或数值比例
 * @returns {Promise<Blob>}
 */
async function exportImageWithRatioPadding(image, ratio) {
  let sourceImage = image;
  let ratioValue = 0;
  let canvas = null;
  let ctx = null;

  if (typeof sourceImage === 'string') {
    sourceImage = await loadImage(sourceImage);
  }

  if (!sourceImage) {
    throw new Error('image is required');
  }

  if (typeof ratio === 'number') {
    ratioValue = ratio;
  } else if (typeof ratio === 'string') {
    let ratioText = ratio.trim();
    if (ratioText.indexOf(':') > -1) {
      let ratioParts = ratioText.split(':');
      let ratioWidth = Number(ratioParts[0]);
      let ratioHeight = Number(ratioParts[1]);
      if (ratioWidth > 0 && ratioHeight > 0) {
        ratioValue = ratioWidth / ratioHeight;
      }
    } else {
      ratioValue = Number(ratioText);
    }
  }

  if (!(ratioValue > 0)) {
    throw new Error('ratio is invalid');
  }

  let sourceWidth =
    sourceImage.naturalWidth || sourceImage.videoWidth || sourceImage.width;
  let sourceHeight =
    sourceImage.naturalHeight || sourceImage.videoHeight || sourceImage.height;

  if (!(sourceWidth > 0) || !(sourceHeight > 0)) {
    throw new Error('image size is invalid');
  }

  let sourceRatio = sourceWidth / sourceHeight;
  let canvasWidth = sourceWidth;
  let canvasHeight = sourceHeight;

  if (Math.abs(sourceRatio - ratioValue) > 0.0001) {
    if (sourceRatio < ratioValue) {
      canvasWidth = Math.round(sourceHeight * ratioValue);
    } else {
      canvasHeight = Math.round(sourceWidth / ratioValue);
    }
  }

  let offsetX = Math.round((canvasWidth - sourceWidth) / 2);
  let offsetY = Math.round((canvasHeight - sourceHeight) / 2);

  canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('canvas context is unavailable');
  }

  ctx.clearRect(0, 0, canvasWidth, canvasHeight);
  ctx.drawImage(sourceImage, offsetX, offsetY, sourceWidth, sourceHeight);

  function dataURLToBlob(dataURL) {
    let parts = dataURL.split(',');
    let mimeMatch = parts[0].match(/:(.*?);/);
    let mime = mimeMatch && mimeMatch[1] ? mimeMatch[1] : 'image/png';
    let binary = atob(parts[1]);
    let length = binary.length;
    let array = new Uint8Array(length);
    let index = 0;

    for (index = 0; index < length; index++) {
      array[index] = binary.charCodeAt(index);
    }

    return new Blob([array], { type: mime });
  }

  function releaseCanvas() {
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
    ctx = null;
    canvas = null;
  }

  return new Promise((resolve, reject) => {
    if (canvas.toBlob) {
      canvas.toBlob((blob) => {
        releaseCanvas();
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error('canvas export failed'));
      }, 'image/png');
      return;
    }

    try {
      let dataURL = canvas.toDataURL('image/png');
      let blob = dataURLToBlob(dataURL);
      releaseCanvas();
      resolve(blob);
    } catch (error) {
      releaseCanvas();
      reject(error);
    }
  });
}

/**
 * 填充图片中非透明像素的颜色
 *
 * 场景说明：
 * 将抠图后的透明背景图片，填充指定颜色，生成色纸预览效果。
 * 保留原图的透明度信息，只改变RGB颜色值。
 *
 * 应用场景：
 * - 色纸预览：用户选择不同颜色的色纸，实时预览效果
 * - 颜色替换：将图片的非透明部分统一替换为指定颜色
 *
 * 处理流程：
 * 1. 加载原图并根据最大尺寸限制进行缩放
 * 2. 遍历所有像素点
 * 3. 对于非透明像素（alpha > 0），替换RGB值为指定颜色
 * 4. 保留原始的alpha透明度
 * 5. 导出为base64图片
 *
 * @param {Object} params - 参数对象
 * @param {string} params.imageSrc - 源图片的URL或base64字符串
 * @param {string} params.rgbaString - RGBA颜色字符串，格式为"rgba(r,g,b,a)"，其中a为0-1之间的透明度值
 * @param {number} params.maxWidth - 图片的最大宽度，用于缩放控制
 * @param {number} params.maxHeight - 图片的最大高度，用于缩放控制
 * @returns {Promise<string>} 返回处理后的图片 blob URL
 */
export async function fillNonTransparentPixels({
  imageSrc,
  rgbaString,
  maxWidth,
  maxHeight,
  cache: useCache = true
  // minTransparentAlpha = 0
}) {
  let startTime = new Date();
  logger.info('start fillNonTransparentPixels');
  if (!maxWidth) {
    let isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIos) {
      // ios 处理较慢
      maxWidth = 600;
      maxHeight = 600;
    } else {
      maxWidth = 1500;
      maxHeight = 1500;
    }
  }
  let minTransparentAlpha = 0;
  // 缓存逻辑
  if (useCache) {
    const cacheKey = JSON.stringify({
      imageSrc,
      rgbaString,
      maxWidth,
      maxHeight
    });
    const cachedResult = fillNonTransparentPixelsCache.get(cacheKey);
    if (cachedResult) {
      return Promise.resolve(cachedResult);
    }
  }

  const fillColor = rgbaString
    .substring(rgbaString.indexOf('(') + 1, rgbaString.lastIndexOf(')'))
    .split(',')
    .map((value) => parseInt(value.trim()));

  fillColor[3] = parseInt(fillColor[3] * 255);
  let img = await loadImage(imageSrc);

  // 计算缩放比例
  let width = img.width;
  let height = img.height;
  let scale = 1;
  if (width > maxWidth || height > maxHeight) {
    scale = Math.min(maxWidth / width, maxHeight / height);
    width *= scale;
    height *= scale;
  }

  // 创建第一个canvas用于绘制原始图片
  let canvas1 = document.createElement('canvas');
  canvas1.width = width;
  canvas1.height = height;
  let ctx1 = canvas1.getContext('2d');
  ctx1.drawImage(img, 0, 0, width, height);

  // 创建第二个canvas用于填充颜色
  let canvas2 = document.createElement('canvas');
  canvas2.width = width;
  canvas2.height = height;
  let ctx2 = canvas2.getContext('2d');

  // 获取第一个canvas的图像数据
  let imageData = ctx1.getImageData(0, 0, width, height);
  const data = imageData.data;

  // 遍历所有像素

  for (let i = 0; i < data.length; i += 4) {
    if (minTransparentAlpha) {
      // 检查像素是否非透明
      if (data[i + 3] > minTransparentAlpha) {
        // 填充颜色
        data[i] = fillColor[0]; // R
        data[i + 1] = fillColor[1]; // G
        data[i + 2] = fillColor[2]; // B
        // data[i + 3] = fillColor[3]; // A
        data[i + 3] = data[i + 3]; // A  保留原始图片的透明度
      } else {
        data[i] = fillColor[0]; // R
        data[i + 1] = fillColor[1]; // G
        data[i + 2] = fillColor[2]; // B
        // data[i + 3] = fillColor[3]; // A
        data[i + 3] = 0;
      }
    } else {
      if (data[i + 3] > 0) {
        // 填充颜色
        data[i] = fillColor[0]; // R
        data[i + 1] = fillColor[1]; // G
        data[i + 2] = fillColor[2]; // B
        // data[i + 3] = fillColor[3]; // A
        data[i + 3] = data[i + 3]; // A  保留原始图片的透明度
      }
    }
  }

  // 将处理后的图像数据放入第二个canvas
  ctx2.putImageData(imageData, 0, 0);

  let res = await canvasToBlobUrl(canvas2, 'image/png');
  // 释放内存
  img.src = '';
  img = null;
  imageData = null;
  canvas1 = null;
  canvas2 = null;
  ctx1 = null;
  ctx2 = null;

  if (scale != 1) {
    res = await zoomImageByCanvas({ imageSource: res, scale: 1 / scale });
  }

  // 存入缓存
  if (useCache) {
    const cacheKey = JSON.stringify({
      imageSrc,
      rgbaString,
      maxWidth,
      maxHeight
    });
    fillNonTransparentPixelsCache.set(cacheKey, res);
  }

  logger.info('fillNonTransparentPixels耗时:' + (new Date() - startTime));

  return res;
}

/**
 * 使用 Canvas 放大图片并返回放大后的图片数据
 * @param {string|File|HTMLImageElement} imageSource - 图片源（url / 本地文件 / img元素）
 * @param {number} scale - 放大倍数（例如 2 = 2倍放大）
 * @param {string} type - 输出图片类型 'image/png' | 'image/jpeg' (默认png)
 * @param {number} quality - 图片质量 0~1 (仅jpeg生效)
 * @returns {Promise<string>} 返回放大后的 base64 图片
 */
async function zoomImageByCanvas({
  imageSource,
  scale,
  type = 'image/png',
  quality = 1.0
}) {
  // 1. 创建图片对象并加载
  const img = await loadImage(imageSource);

  // 2. 计算放大后的尺寸
  const originalWidth = img.naturalWidth || img.width;
  const originalHeight = img.naturalHeight || img.height;
  const zoomWidth = originalWidth * scale;
  const zoomHeight = originalHeight * scale;

  // 3. 创建 Canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // 关键：设置 Canvas 尺寸 = 放大后的尺寸
  canvas.width = zoomWidth;
  canvas.height = zoomHeight;

  // 4. 绘制放大图片（清晰绘制，无模糊）
  ctx.drawImage(
    img,
    0,
    0,
    originalWidth,
    originalHeight, // 原图区域
    0,
    0,
    zoomWidth,
    zoomHeight // 目标放大区域
  );

  return canvasToBlobUrl(canvas, type);
}

/**
 * 将黑白图片中的纯黑像素替换为指定HEX颜色，生成新的PNG base64
 *
 * 规则说明：
 * - 仅处理纯黑像素：R=0、G=0、B=0 且 A>0
 * - 仅替换RGB，保留原图每个像素的alpha透明度
 * - 支持 maxWidth/maxHeight 等比缩放限制
 *
 * @param {Object} params - 参数对象
 * @param {string} params.imageSrc - 源图片的URL或base64字符串
 * @param {string} params.hexColor - HEX颜色字符串，格式为"#RGB"或"#RRGGBB"
 * @param {number} params.maxWidth - 图片的最大宽度，用于缩放控制
 * @param {number} params.maxHeight - 图片的最大高度，用于缩放控制
 * @param {boolean} params.cache - 是否启用缓存，默认 true
 * @returns {Promise<string>} 返回处理后的图片 blob URL（PNG）
 */
export async function fillBlackPixelsWithHexColor({
  imageSrc,
  hexColor,
  maxWidth,
  maxHeight,
  cache: useCache = true
}) {
  // 缓存逻辑
  if (useCache) {
    const cacheKey = JSON.stringify({
      imageSrc,
      hexColor,
      maxWidth,
      maxHeight
    });
    const cachedResult = fillBlackPixelsWithHexColorCache.get(cacheKey);
    if (cachedResult) {
      return Promise.resolve(cachedResult);
    }
  }

  const fillColor = parseHexColorToRgb(hexColor);
  let img = await loadImage(imageSrc);

  // 计算缩放比例
  let width = img.width;
  let height = img.height;
  if (width > maxWidth || height > maxHeight) {
    const scale = Math.min(maxWidth / width, maxHeight / height);
    width *= scale;
    height *= scale;
  }

  // 创建第一个canvas用于绘制原始图片
  let canvas1 = document.createElement('canvas');
  canvas1.width = width;
  canvas1.height = height;
  let ctx1 = canvas1.getContext('2d');
  ctx1.drawImage(img, 0, 0, width, height);

  // 创建第二个canvas用于输出
  let canvas2 = document.createElement('canvas');
  canvas2.width = width;
  canvas2.height = height;
  let ctx2 = canvas2.getContext('2d');

  // 获取第一个canvas的图像数据
  let imageData = ctx1.getImageData(0, 0, width, height);
  const data = imageData.data;

  // 遍历所有像素：仅替换纯黑像素的RGB，保留alpha
  for (let i = 0; i < data.length; i += 4) {
    if (
      data[i + 3] > 0 &&
      data[i] === 0 &&
      data[i + 1] === 0 &&
      data[i + 2] === 0
    ) {
      data[i] = fillColor[0]; // R
      data[i + 1] = fillColor[1]; // G
      data[i + 2] = fillColor[2]; // B
      data[i + 3] = data[i + 3]; // A 保留原始图片的透明度
    }
  }

  // 将处理后的图像数据放入第二个canvas
  ctx2.putImageData(imageData, 0, 0);

  let res = await canvasToBlobUrl(canvas2, 'image/png');

  // 释放内存
  img.src = '';
  img = null;
  imageData = null;
  canvas1 = null;
  canvas2 = null;
  ctx1 = null;
  ctx2 = null;

  // 存入缓存
  if (useCache) {
    const cacheKey = JSON.stringify({
      imageSrc,
      hexColor,
      maxWidth,
      maxHeight
    });
    fillBlackPixelsWithHexColorCache.set(cacheKey, res);
  }

  return res;
}

/**
 * 使用 globalCompositeOperation 将透明图中的非透明区域整体替换为指定颜色（推荐用于“透明底黑色图形”染色）
 *
 * 原理说明：
 * 1. 先绘制原图得到 alpha 遮罩
 * 2. 设置 globalCompositeOperation = 'source-in'
 * 3. 在画布上填充目标颜色，颜色只会出现在已有 alpha 的区域
 *
 * 特点：
 * - 会处理抗锯齿边缘的半透明像素，避免出现黑边/灰边
 * - 不区分原像素是否“纯黑”，而是按“是否非透明”整体替换颜色
 *
 * @param {Object} params - 参数对象
 * @param {string} params.imageSrc - 源图片的URL或base64字符串
 * @param {string} params.hexColor - HEX颜色字符串，格式为"#RGB"或"#RRGGBB"
 * @param {number} params.maxWidth - 图片的最大宽度，用于缩放控制
 * @param {number} params.maxHeight - 图片的最大高度，用于缩放控制
 * @param {boolean} params.cache - 是否启用缓存，默认 true
 * @returns {Promise<string>} 返回处理后的图片 blob URL（PNG）
 */
export async function fillBlackPixelsWithHexColorByComposite({
  imageSrc,
  hexColor,
  maxWidth,
  maxHeight,
  cache: useCache = true
}) {
  // 缓存逻辑
  if (useCache) {
    const cacheKey = JSON.stringify({
      imageSrc,
      hexColor,
      maxWidth,
      maxHeight
    });
    const cachedResult = fillBlackPixelsWithHexColorByCompositeCache.get(
      cacheKey
    );
    if (cachedResult) {
      return Promise.resolve(cachedResult);
    }
  }

  const rgb = parseHexColorToRgb(hexColor);
  const fillStyle = 'rgb(' + rgb[0] + ',' + rgb[1] + ',' + rgb[2] + ')';

  let img = await loadImage(imageSrc);

  // 计算缩放比例
  let width = img.width;
  let height = img.height;
  if (width > maxWidth || height > maxHeight) {
    const scale = Math.min(maxWidth / width, maxHeight / height);
    width *= scale;
    height *= scale;
  }

  // 创建canvas并绘制原图（作为 alpha 遮罩）
  let canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  let ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  // 使用 source-in：只在已有 alpha 区域填充颜色
  ctx.globalCompositeOperation = 'source-in';
  ctx.fillStyle = fillStyle;
  ctx.fillRect(0, 0, width, height);
  ctx.globalCompositeOperation = 'source-over';

  let res = await canvasToBlobUrl(canvas, 'image/png');

  // 释放内存
  img.src = '';
  img = null;
  canvas = null;
  ctx = null;

  // 存入缓存
  if (useCache) {
    const cacheKey = JSON.stringify({
      imageSrc,
      hexColor,
      maxWidth,
      maxHeight
    });
    fillBlackPixelsWithHexColorByCompositeCache.set(cacheKey, res);
  }

  return res;
}

/**
 * 将图片长边撑满在canvas中，另一边居中
 *
 * 场景说明：
 * 在固定尺寸的canvas中展示图片，确保图片完整显示且尽可能大。
 * 类似CSS的 object-fit: contain 效果。
 *
 * 计算逻辑：
 * 1. 计算图片缩放到canvas的比例（取宽度比和高度比的较小值）
 * 2. 按照该比例缩放图片
 * 3. 将缩放后的图片居中绘制到canvas
 *
 * 举例：
 * - Canvas: 600x400，图片: 1200x600（横向）→ 缩放为 600x300，垂直居中
 * - Canvas: 600x400，图片: 600x1200（竖向）→ 缩放为 200x400，水平居中
 *
 * @param {Object} params - 参数对象
 * @param {CanvasRenderingContext2D} params.ctx - Canvas上下文
 * @param {string} params.imageSrc - 图片源URL
 * @param {number} params.canvasWidth - Canvas宽度
 * @param {number} params.canvasHeight - Canvas高度
 * @param {number} params.opacity - 图片透明度（可选）
 * @param {number} params.imageScale - 图片额外缩放比例，默认1
 * @param {number} params.offsetXPercent - X方向偏移百分比，默认0（0.1表示向右偏移10%画布宽度）
 * @param {number} params.offsetYPercent - Y方向偏移百分比，默认0（0.1表示向下偏移10%画布高度）
 * @returns {Promise<void>}
 */
async function fitImageInCanvas({
  ctx,
  imageSrc,
  canvasWidth,
  canvasHeight,
  opacity,
  imageScale = 1,
  offsetXPercent = 0,
  offsetYPercent = 0
}) {
  // 补全代码
  let img = await loadImage(imageSrc);
  let width = img.width;
  let height = img.height;
  let scale = Math.min(canvasWidth / width, canvasHeight / height);
  let newWidth = width * scale;
  let newHeight = height * scale;

  // 计算居中位置
  const x = (canvasWidth - newWidth) / 2;
  const y = (canvasHeight - newHeight) / 2;

  ctx.save();
  if (opacity) {
    ctx.globalAlpha = opacity;
  }
  // x\y 方向的偏移量
  if (offsetXPercent || offsetYPercent) {
    ctx.translate(offsetXPercent * canvasWidth, offsetYPercent * canvasHeight);
  }
  // 图片缩放比例
  if (imageScale != 1) {
    ctx.scale(imageScale, imageScale);
  }
  ctx.drawImage(img, x, y, newWidth, newHeight);
  ctx.restore();
}

/**
 * 使用蒙版绘制图片（实现白墨叠加效果）
 *
 * 场景说明：
 * 白墨功能 - 在透明图片上叠加白墨图案。白墨区域保持原图不透明，非白墨区域降低透明度。
 * 实现类似印刷中的白墨效果，让图案在透明或半透明材质上清晰可见。
 *
 * 工作原理：
 * 1. 加载蒙版图（白墨图）和目标图（轮廓图）
 * 2. 将两张图都居中缩放到canvas尺寸
 * 3. 逐像素处理：
 *    - 如果蒙版对应位置有白墨（alpha > 0）：保持目标图原透明度
 *    - 如果蒙版对应位置无白墨（alpha = 0）：降低目标图透明度到指定值
 * 4. 生成合成后的图片
 *
 * 应用效果：
 * 白墨区域：100%不透明，图案清晰
 * 非白墨区域：半透明（如30%），形成对比效果
 *
 * @param {Object} params - 参数对象
 * @param {CanvasRenderingContext2D} params.ctx - Canvas上下文（可选，不提供则创建临时画布）
 * @param {string} params.whiteInkImageSrc - 蒙版图片源（白墨图片URL）
 * @param {string} params.outlineImageSrc - 目标图片源（轮廓图URL）
 * @param {number} params.canvasWidth - Canvas宽度
 * @param {number} params.canvasHeight - Canvas高度
 * @param {number} params.opacity - 非白墨区域的目标图片透明度，默认1
 * @param {boolean} params.exportImage - 是否导出图片，默认false
 * @returns {Promise<string|void>} 如果exportImage为true，返回图片 blob URL，否则无返回值
 */
export async function drawImageWithMask({
  ctx,
  whiteInkImageSrc,
  outlineImageSrc,
  canvasWidth,
  canvasHeight,
  opacity = 1,
  exportImage = false
}) {
  let canvasNode;
  if (!ctx) {
    const { ctx: newCtx, canvasNode: newCanvasNode } = await initCanvas({
      dom: document.createElement('canvas'),
      canvasWidth,
      canvasHeight
    });
    ctx = newCtx;
    canvasNode = newCanvasNode;
  }

  // 加载蒙版图片和目标图片
  const [whiteInkImg, outlineImg] = await Promise.all([
    loadImage(whiteInkImageSrc),
    loadImage(outlineImageSrc)
  ]);

  // 计算蒙版图片的缩放参数和居中位置
  const maskScale = Math.min(
    canvasWidth / whiteInkImg.width,
    canvasHeight / whiteInkImg.height
  );
  const maskWidth = whiteInkImg.width * maskScale;
  const maskHeight = whiteInkImg.height * maskScale;
  const maskX = (canvasWidth - maskWidth) / 2;
  const maskY = (canvasHeight - maskHeight) / 2;

  // 计算目标图片的缩放参数和居中位置
  const targetScale = Math.min(
    canvasWidth / outlineImg.width,
    canvasHeight / outlineImg.height
  );

  const targetWidth = outlineImg.width * targetScale;
  const targetHeight = outlineImg.height * targetScale;
  const targetX = (canvasWidth - targetWidth) / 2;
  const targetY = (canvasHeight - targetHeight) / 2;

  // 获取设备像素比
  const dpr = window.devicePixelRatio || 1;
  const actualWidth = Math.floor(canvasWidth * dpr);
  const actualHeight = Math.floor(canvasHeight * dpr);

  // 创建临时canvas用于处理蒙版
  const maskCanvas = document.createElement('canvas');
  const { ctx: maskCtx, canvasNode: maskCanvasNode } = await initCanvas({
    dom: maskCanvas,
    canvasWidth,
    canvasHeight
  });

  // 绘制蒙版图片到临时canvas（居中）
  maskCtx.drawImage(whiteInkImg, maskX, maskY, maskWidth, maskHeight);

  // 获取蒙版的图像数据 - 使用实际像素尺寸
  const maskImageData = maskCtx.getImageData(0, 0, actualWidth, actualHeight);
  const maskData = maskImageData.data;

  // 创建目标图片的临时canvas
  const targetCanvas = document.createElement('canvas');
  const { ctx: targetCtx, canvasNode: targetCanvasNode } = await initCanvas({
    dom: targetCanvas,
    canvasWidth,
    canvasHeight
  });

  // 绘制目标图片到临时canvas（居中）
  targetCtx.drawImage(outlineImg, targetX, targetY, targetWidth, targetHeight);

  // 获取目标图片的图像数据 - 使用实际像素尺寸
  const targetImageData = targetCtx.getImageData(
    0,
    0,
    actualWidth,
    actualHeight
  );
  const targetData = targetImageData.data;

  // 创建最终结果的图像数据 - 使用实际像素尺寸
  const resultImageData = ctx.createImageData(actualWidth, actualHeight);
  const resultData = resultImageData.data;

  // 遍历每个像素点
  for (let i = 0; i < resultData.length; i += 4) {
    const pixelIndex = i / 4;
    const x = pixelIndex % actualWidth;
    const y = Math.floor(pixelIndex / actualWidth);

    // 检查蒙版对应位置是否非透明
    const maskAlpha = maskData[i + 3] > 10;
    resultData[i] = targetData[i]; // R
    resultData[i + 1] = targetData[i + 1]; // G
    resultData[i + 2] = targetData[i + 2]; // B
    resultData[i + 3] = Math.floor(
      targetData[i + 3] * (maskAlpha ? 1 : opacity)
    ); // A
  }

  // 将处理后的图像数据绘制到主canvas - 使用实际像素尺寸
  ctx.putImageData(resultImageData, 0, 0);

  // 清理临时资源
  maskCanvasNode.width = 0;
  maskCanvasNode.height = 0;
  targetCanvasNode.width = 0;
  targetCanvasNode.height = 0;
  if (exportImage) {
    return await canvasToBlobUrl(canvasNode, 'image/png');
  }
}

/**
 * Canvas绘制工具类
 *
 * 核心功能：
 * 提供完整的Canvas图片绘制解决方案，专为照片编辑场景设计。
 *
 * 主要能力：
 * 1. 智能尺寸计算：自动适配不同宽高比的图片到指定区域
 * 2. 背景图绘制：支持彩窗效果（背景图仅在轮廓内显示）
 * 3. 白墨效果：支持蒙版叠加，实现印刷白墨效果
 * 4. 轮廓提取：使用边界跟踪算法提取精确轮廓
 * 5. 兼容性处理：自动检测Path2D支持，提供降级方案
 *
 * 使用场景：
 * - 贴纸编辑：将抠图后的照片绘制到各种尺寸的画布
 * - 预览生成：生成不同规格的预览图
 * - 效果合成：白墨、彩窗等特殊效果
 * - 导出打印：生成符合打印要求的高质量图片
 *
 * 关键概念：
 * - 轮廓图：抠图后的PNG图片（有透明背景）
 * - 原图区域：用户编辑时图片在画布中的显示区域
 * - drawCanvasInfo：记录原图区域的位置和尺寸信息
 * - transformRatio：缩放比例，用于适配不同尺寸的输出画布
 */
export class CanvasDrawer {
  constructor(ctx) {
    this.ctx = ctx;
    // 检查Path2D API支持
    this.supportsPath2D = typeof Path2D !== 'undefined';
    // console.log('Path2D API 支持:', this.supportsPath2D);
  }

  /**
   * 清空画布
   * @param {number} width - 画布宽度
   * @param {number} height - 画布高度
   */
  clearCanvas(width, height) {
    this.ctx.clearRect(0, 0, width, height);
  }

  /**
   * 绘制背景图片（彩窗功能）
   *
   * 场景说明：
   * 彩窗功能 - 在抠图轮廓内填充背景图片（如渐变色、图案等）。
   * 用户在编辑器中调整背景图片的位置、大小、旋转，在新画布中按比例绘制。
   * 背景图片会被裁剪到主图轮廓范围内，形成窗口效果。
   *
   * 坐标系转换：
   * - 原画板：用户编辑器中的画布（如800x600）
   * - 新画板：当前要绘制的目标画布（如400x300，缩放比例0.5）
   * - 需要将原画板中的坐标、尺寸按canvasScale比例转换到新画板
   *
   * 绘制流程：
   * 1. 根据原画板中的配置，计算背景图在新画板中的位置和尺寸
   * 2. 如果提供了clipImage，设置裁剪区域为主图轮廓
   * 3. 绘制背景图（支持旋转）
   * 4. 返回绘制结果信息
   *
   * @param {Object} params - 参数对象
   * @param {Object} params.win - 背景图片配置信息（来自原画板）
   * @param {string} params.win.imageUrl - 背景图片的URL
   * @param {number} params.win.x - 背景图片在原画板中的中心X坐标
   * @param {number} params.win.y - 背景图片在原画板中的中心Y坐标
   * @param {number} params.win.scale - 背景图片在原画板中的缩放比例
   * @param {number} params.win.rotate - 背景图片的旋转角度（度数）
   * @param {number} params.win.opacity - 背景图片的透明度
   * @param {number} params.canvasScale - 画布缩放比例（新画板/原画板）
   * @param {Object} params.drawCanvasInfo - 画布绘制信息
   * @param {number} params.drawCanvasInfo.inCanvasImageWidth - 原图在画布中的宽度
   * @param {number} params.drawCanvasInfo.inCanvasImageHeight - 原图在画布中的高度
   * @param {Object} params.drawCanvasInfo.currStatus - 当前图片状态
   * @param {number} params.drawCanvasInfo.currStatus.x - 图片在画布中的X坐标
   * @param {number} params.drawCanvasInfo.currStatus.y - 图片在画布中的Y坐标
   * @param {number} params.transformRatio - 额外的缩放比例，默认1
   * @param {Image} params.clipImage - 主图片对象，用于计算裁剪区域
   * @returns {Promise<Object>} 绘制结果对象
   * @returns {boolean} returns.success - 是否绘制成功
   * @returns {string} returns.error - 错误信息（失败时）
   * @returns {Object} returns.backgroundInfo - 背景图片信息（成功时）
   */
  async drawBackground(params = {}) {
    const {
      win,
      canvasScale = 1,
      clipImage,
      drawCanvasInfo,
      transformRatio = 1
    } = params;

    let imageInfo = await getImageInfo(win.imageUrl);

    let imageScale = win.scale * canvasScale; // win.scale是 画布/图片原图的缩放比例

    // 计算背景图片在新画板中的尺寸
    const backgroundWidth = imageScale * imageInfo.naturalWidth;
    const backgroundHeight = imageScale * imageInfo.naturalHeight;

    // 计算背景图片在新画板中的中心位置
    const centerX = win.x * canvasScale;
    const centerY = win.y * canvasScale;

    // 计算绘制参数（左上角坐标）
    const drawX = centerX - backgroundWidth / 2;
    const drawY = centerY - backgroundHeight / 2;

    // 保存当前画布状态
    this.ctx.save();

    if (win.opacity !== undefined && win.opacity !== 1) {
      this.ctx.globalAlpha = win.opacity;
    }

    try {
      // 绘制背景图片
      if (win.imageUrl) {
        try {
          // 加载并绘制背景图片
          const img = await this.loadImage(win.imageUrl);
          // debugger;

          // 根据 clipImage 和 drawCanvasInfo 计算绘制区域 C
          // 直接使用 calculateDrawParams 方法确保计算逻辑完全一致
          if (clipImage && drawCanvasInfo) {
            const {
              inCanvasImageWidth,
              inCanvasImageHeight,
              currStatus
            } = drawCanvasInfo;

            // 使用 calculateDrawParams 方法计算绘制参数
            const drawParams = this.calculateDrawParams({
              outlineImageRatio: clipImage.width / clipImage.height,
              originImageRatio: inCanvasImageWidth / inCanvasImageHeight,
              inCanvasImageWidth,
              inCanvasImageHeight,
              currStatus,
              transformRatio
            });

            // 使用 calculateDrawParams 的结果设置裁剪区域
            this.ctx.beginPath();
            this.ctx.rect(
              drawParams.dX,
              drawParams.dY,
              drawParams.dWidth,
              drawParams.dHeight
            );
            this.ctx.clip();
          }

          // 如果存在旋转角度，设置旋转变换
          // debugger;
          if (win.rotate && win.rotate !== 0) {
            // 将角度转换为弧度
            const rotateRadians = (win.rotate * Math.PI) / 180;

            this.ctx.save();
            // 移动到旋转中心点
            this.ctx.translate(centerX, centerY);

            // 执行旋转
            this.ctx.rotate(rotateRadians);

            // 修改代码
            this.ctx.drawImage(
              img,
              -backgroundWidth / 2,
              -backgroundHeight / 2,
              backgroundWidth,
              backgroundHeight
            );
            this.ctx.restore();
          } else {
            this.ctx.drawImage(
              img,
              drawX,
              drawY,
              backgroundWidth,
              backgroundHeight
            );
          }
        } catch (imageError) {
          console.warn('背景图片加载失败，绘制占位矩形:', imageError);
        }
      }

      return {
        success: true,
        backgroundInfo: {
          drawX,
          drawY,
          width: backgroundWidth,
          height: backgroundHeight,
          centerX,
          centerY,
          scale: canvasScale,
          rotate: win.rotate || 0
        }
      };
    } catch (error) {
      console.error('绘制背景失败:', error);
      return {
        success: false,
        error: error.message
      };
    } finally {
      // 恢复画布状态
      this.ctx.restore();
    }
  }

  /**
   * 加载图片的辅助方法
   * @param {string} imageSrc - 图片链接
   * @returns {Promise<HTMLImageElement>} 加载完成的图片对象
   */
  loadImage(imageSrc) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imageSrc;
    });
  }

  /**
   * 计算轮廓图在画布中的绘制参数
   *
   * 场景说明：
   * 用户上传原图后，经过抠图处理得到轮廓图。轮廓图的宽高比可能与原图不同。
   * 此函数的作用是：将轮廓图完整地绘制到原图的显示区域内，并居中对齐。
   *
   * 计算逻辑：
   * 1. 比较轮廓图和原图的宽高比
   * 2. 如果轮廓图更宽：以宽度为基准适配，高度按比例缩放，然后垂直居中
   * 3. 如果轮廓图更高：以高度为基准适配，宽度按比例缩放，然后水平居中
   *
   * 举例：
   * - 原图显示区域：400x300（宽高比 1.33）
   * - 轮廓图A：600x300（宽高比 2.0，更宽）→ 绘制为 400x200，垂直居中
   * - 轮廓图B：300x600（宽高比 0.5，更高）→ 绘制为 150x300，水平居中
   *
   * @param {Object} params - 参数对象
   * @param {number} params.outlineImageRatio - 轮廓图的宽高比（宽度/高度）
   * @param {number} params.originImageRatio - 原图在画布中的宽高比（宽度/高度）
   * @param {number} params.inCanvasImageWidth - 原图在画布中的显示宽度
   * @param {number} params.inCanvasImageHeight - 原图在画布中的显示高度
   * @param {Object} params.currStatus - 当前图片状态
   * @param {number} params.currStatus.x - 原图在画布中的X坐标
   * @param {number} params.currStatus.y - 原图在画布中的Y坐标
   * @param {number} params.transformRatio - 缩放比例（当前画布尺寸/原始画布尺寸），默认1
   *
   * @returns {Object} 绘制参数对象
   * @returns {number} returns.dWidth - 轮廓图的绘制宽度
   * @returns {number} returns.dHeight - 轮廓图的绘制高度
   * @returns {number} returns.dX - 轮廓图的绘制起始X坐标（左上角）
   * @returns {number} returns.dY - 轮廓图的绘制起始Y坐标（左上角）
   */
  calculateDrawParams({
    outlineImageRatio,
    originImageRatio,
    inCanvasImageWidth,
    inCanvasImageHeight,
    currStatus,
    transformRatio = 1
  }) {
    let dWidth, dHeight, dX, dY;

    if (outlineImageRatio > originImageRatio) {
      // 情况1：轮廓图更宽（如横向照片），以宽度为基准适配

      // 宽度：直接使用原图显示区域的宽度
      dWidth = inCanvasImageWidth * transformRatio;

      // 高度：根据轮廓图的宽高比计算（高度 = 宽度 / 宽高比）
      dHeight = dWidth / outlineImageRatio;

      // X坐标：与原图左边对齐
      dX = currStatus.x * transformRatio;

      // Y坐标：在原图显示区域内垂直居中
      // 计算公式：原图Y坐标 + (原图高度 - 轮廓图高度) / 2
      dY =
        currStatus.y * transformRatio +
        (inCanvasImageHeight * transformRatio - dHeight) / 2;
    } else {
      // 情况2：轮廓图更高（如竖向照片），以高度为基准适配

      // 高度：直接使用原图显示区域的高度
      dHeight = inCanvasImageHeight * transformRatio;

      // 宽度：根据轮廓图的宽高比计算（宽度 = 高度 × 宽高比）
      dWidth = dHeight * outlineImageRatio;

      // Y坐标：与原图顶部对齐
      dY = currStatus.y * transformRatio;

      // X坐标：在原图显示区域内水平居中
      // 计算公式：原图X坐标 + (原图宽度 - 轮廓图宽度) / 2
      dX =
        currStatus.x * transformRatio +
        (inCanvasImageWidth * transformRatio - dWidth) / 2;
    }

    return { dWidth, dHeight, dX, dY };
  }

  /**
   * 绘制图片到画布
   *
   * 功能说明：
   * 基础的图片绘制方法，封装了ctx.drawImage()调用和状态管理。
   *
   * 状态管理：
   * saveRestore=true时，会自动保存和恢复画布状态（ctx.save/restore）。
   * 这样可以避免绘制操作影响后续的绘制（如透明度、变换等）。
   *
   * 使用场景：
   * 作为底层绘制方法，被其他高级绘制方法调用。
   *
   * @param {Object} params - 参数对象
   * @param {HTMLImageElement} params.image - 要绘制的图片对象
   * @param {number} params.dX - 绘制起始X坐标（左上角）
   * @param {number} params.dY - 绘制起始Y坐标（左上角）
   * @param {number} params.dWidth - 绘制宽度
   * @param {number} params.dHeight - 绘制高度
   * @param {boolean} params.saveRestore - 是否使用save/restore包裹绘制，默认true
   */
  drawImage({ image, dX, dY, dWidth, dHeight, saveRestore = true }) {
    if (saveRestore) {
      this.ctx.save();
    }

    this.ctx.drawImage(image, dX, dY, dWidth, dHeight);

    if (saveRestore) {
      this.ctx.restore();
    }
  }

  /**
   * 转换drawCanvasInfo对象，应用缩放比例
   *
   * 场景说明：
   * 当需要在不同尺寸的画布上绘制时，需要将原始drawCanvasInfo按比例缩放。
   *
   * 使用场景：
   * - 预览图生成：原画布800x600，生成缩略图400x300（transformRatio=0.5）
   * - 打印输出：原画布800x600，生成高清图1600x1200（transformRatio=2）
   * - 响应式适配：根据屏幕尺寸动态调整
   *
   * 转换逻辑：
   * 将drawCanvasInfo中的所有尺寸和坐标值乘以transformRatio
   *
   * 注意：
   * 使用JSON深拷贝，避免修改原对象
   *
   * @param {Object} drawCanvasInfo - 画布绘制信息
   * @param {number} drawCanvasInfo.inCanvasImageWidth - 原图在画布中的宽度
   * @param {number} drawCanvasInfo.inCanvasImageHeight - 原图在画布中的高度
   * @param {Object} drawCanvasInfo.currStatus - 当前图片状态
   * @param {number} drawCanvasInfo.currStatus.x - 图片在画布中的X坐标
   * @param {number} drawCanvasInfo.currStatus.y - 图片在画布中的Y坐标
   * @param {number} transformRatio - 缩放比例（目标画布尺寸/原始画布尺寸）
   * @returns {Object} 转换后的drawCanvasInfo对象（新对象，不影响原对象）
   */
  transformDrawCanvasInfo(drawCanvasInfo, transformRatio) {
    let data = JSON.parse(JSON.stringify(drawCanvasInfo));
    data.inCanvasImageWidth = data.inCanvasImageWidth * transformRatio;
    data.inCanvasImageHeight = data.inCanvasImageHeight * transformRatio;
    data.currStatus.x = data.currStatus.x * transformRatio;
    data.currStatus.y = data.currStatus.y * transformRatio;
    return data;
  }

  /**
   * 执行完整的图片绘制流程
   *
   * 场景说明：
   * 这是核心绘制方法，将轮廓图绘制到画布上。
   * 支持白墨效果：如果drawCanvasInfo包含whiteInk，会应用白墨蒙版处理。
   *
   * 绘制流程：
   * 1. 可选：清空画布
   * 2. 从drawCanvasInfo获取原图显示区域信息
   * 3. 计算轮廓图的绘制参数（位置和尺寸）
   * 4. 判断是否有白墨：
   *    - 有白墨：使用drawImageWithMask合成白墨效果后绘制
   *    - 无白墨：直接绘制轮廓图
   * 5. 返回绘制参数和图片信息
   *
   * 关键概念：
   * - 轮廓图：抠图后的PNG图片
   * - drawCanvasInfo：记录了原图在画布中的显示信息（位置、尺寸）
   * - transformRatio：当前画布与原始画布的缩放比例
   *
   * @param {Object} params - 参数对象
   * @param {Image} params.image - 要绘制的轮廓图对象
   * @param {number} params.canvasWidth - 当前画布宽度
   * @param {number} params.canvasHeight - 当前画布高度
   * @param {Object} params.drawCanvasInfo - 画布绘制信息
   * @param {number} params.drawCanvasInfo.inCanvasImageWidth - 原图在画布中的宽度
   * @param {number} params.drawCanvasInfo.inCanvasImageHeight - 原图在画布中的高度
   * @param {Object} params.drawCanvasInfo.currStatus - 当前图片状态
   * @param {number} params.drawCanvasInfo.currStatus.x - 图片在画布中的X坐标
   * @param {number} params.drawCanvasInfo.currStatus.y - 图片在画布中的Y坐标
   * @param {string} params.drawCanvasInfo.whiteInk - 白墨图片URL（可选）
   * @param {number} params.drawCanvasInfo.canvasWidth - 原始画布宽度
   * @param {number} params.drawCanvasInfo.canvasHeight - 原始画布高度
   * @param {number} params.transformRatio - 缩放比例，默认1
   * @param {boolean} params.enableDebug - 是否启用调试输出，默认false
   * @param {boolean} params.isClearCanvas - 是否清空画布，默认true
   *
   * @returns {Object} 绘制结果对象
   * @returns {Object} returns.drawParams - 绘制参数（dX, dY, dWidth, dHeight）
   * @returns {Object} returns.imageInfo - 图片信息（宽高比等）
   */
  async drawImageToCanvas({
    image,
    canvasWidth,
    canvasHeight,
    drawCanvasInfo,
    transformRatio = 1,
    enableDebug = false,
    isClearCanvas = true
  }) {
    // 清空画布
    if (isClearCanvas) {
      this.clearCanvas(canvasWidth, canvasHeight);
    }

    // 获取必要的绘制信息
    const {
      inCanvasImageWidth,
      inCanvasImageHeight,
      currStatus
    } = drawCanvasInfo;

    // 计算图片比例
    const outlineImageRatio = image.width / image.height;
    const originImageRatio = inCanvasImageWidth / inCanvasImageHeight;
    // 计算绘制参数
    const drawParams = this.calculateDrawParams({
      outlineImageRatio,
      originImageRatio,
      inCanvasImageWidth,
      inCanvasImageHeight,
      currStatus,
      transformRatio
    });

    if (drawCanvasInfo.whiteInk) {
      console.log('drawCanvasInfo.whiteInk', drawCanvasInfo.whiteInk);
      console.log('image.src', image.src);
      // debugger;

      // 有白墨时, 需要设置主图透明度 (为了显示白墨叠加效果)
      // const whiteInkImage = await this.loadImage(drawCanvasInfo.whiteInk);

      let imageSrc = await drawImageWithMask({
        whiteInkImageSrc: drawCanvasInfo.whiteInk,
        outlineImageSrc: image.src,
        canvasWidth,
        canvasHeight,
        opacity: config.imageWithWhiteInkDefaultOpacity,
        exportImage: true
      });

      window._debugLog({
        title: '测试2',
        'drawCanvasInfo.whiteInk': drawCanvasInfo.whiteInk,
        outlineImageSrc: image.src
      });

      let img = await this.loadImage(imageSrc);
      // console.log(`canvasWidth=${canvasWidth},canvasHeight=${canvasHeight}`);
      // downloadFile(imageSrc, 'whiteInk.png');
      this.ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

      this.ctx.restore();
    } else {
      // 绘制图片
      this.drawImage({
        image,
        dX: drawParams.dX,
        dY: drawParams.dY,
        dWidth: drawParams.dWidth,
        dHeight: drawParams.dHeight,
        saveRestore: true
      });
    }

    // 调试信息输出
    if (enableDebug) {
      console.log('CanvasDrawer 绘制参数:', {
        canvasSize: { width: canvasWidth, height: canvasHeight },
        drawCanvasInfoSize: {
          width: drawCanvasInfo.canvasWidth,
          height: drawCanvasInfo.canvasHeight
        },
        transformRatio,
        imageSize: { width: image.width, height: image.height },
        outlineImageRatio,
        originImageRatio,
        drawParams,
        currStatus
      });
    }

    return {
      drawParams,
      imageInfo: {
        outlineImageRatio,
        originImageRatio,
        transformRatio
      }
    };
  }

  /**
   * 执行完整的绘制流程（通用方法 - 入口函数）
   *
   * 功能说明：
   * 这是最高级别的绘制入口方法，根据是否有彩窗自动选择绘制策略。
   *
   * 工作流程：
   * 1. 验证画布尺寸（四舍五入，检查非空）
   * 2. 判断是否有彩窗（acrylicWindow.imageUrl）
   *    - 有彩窗：调用 executeFullDrawFlowWithBackground
   *    - 无彩窗：调用 executeFullDrawFlowWithoutBackground
   * 3. 返回统一格式的绘制结果
   *
   * 使用场景：
   * - 预览图生成：在指定尺寸画布上生成预览
   * - 打印输出：生成高分辨率的打印图片
   * - 效果合成：应用白墨、彩窗等效果
   *
   * @param {Object} params - 参数对象
   * @param {Object} params.ctx - Canvas上下文（可选，不提供则创建临时画布）
   * @param {number} params.canvasWidth - 画布宽度
   * @param {number} params.canvasHeight - 画布高度
   * @param {Object} params.drawCanvasInfo - 画布绘制信息
   * @param {number} params.drawCanvasInfo.inCanvasImageWidth - 原图在画布中的宽度
   * @param {number} params.drawCanvasInfo.inCanvasImageHeight - 原图在画布中的高度
   * @param {Object} params.drawCanvasInfo.currStatus - 当前图片状态
   * @param {string} params.imageSrc - 主图片的URL
   * @param {Object} params.acrylicWindow - 彩窗配置（可选）
   * @param {string} params.acrylicWindow.imageUrl - 背景图片的URL
   *
   * @returns {Promise<Object>} 绘制结果对象
   * @returns {Object} returns.drawParams - 绘制参数（dX, dY, dWidth, dHeight）
   * @returns {Object} returns.imageInfo - 图片信息（宽高比等）
   * @returns {HTMLCanvasElement} returns.canvas - 绘制完成的画布
   * @returns {CanvasRenderingContext2D} returns.ctx - 绘制上下文
   */
  async executeFullDrawFlow({
    ctx = null,
    canvasWidth,
    canvasHeight,
    drawCanvasInfo,
    imageSrc,
    acrylicWindow = null,
    fillBackground = false,
    isRound = false,
    borderRadius = 0
  }) {
    canvasWidth = Math.round(canvasWidth);
    canvasHeight = Math.round(canvasHeight);

    if (!canvasWidth || !canvasHeight) {
      throw new Error('canvasWidth和canvasHeight不能为空');
    }

    // 根据是否有背景图片选择不同的绘制方法
    if (acrylicWindow && acrylicWindow.imageUrl) {
      // 有彩窗的绘制
      return this.executeFullDrawFlowWithBackground({
        ctx,
        canvasWidth,
        canvasHeight,
        drawCanvasInfo,
        imageSrc,
        acrylicWindow,
        fillBackground,
        isRound,
        borderRadius
      });
    } else {
      // 没有彩窗
      return this.executeFullDrawFlowWithoutBackground({
        ctx,
        canvasWidth,
        canvasHeight,
        drawCanvasInfo,
        imageSrc,
        isRound,
        borderRadius
      });
    }
  }

  /**
   * 执行无背景图片的完整绘制流程（普通模式）
   *
   * 场景说明：
   * 只绘制主图片，不添加背景。适用于大部分常规场景。
   * 如果drawCanvasInfo包含whiteInk，会应用白墨效果。
   *
   * 绘制流程：
   * 1. 创建或使用提供的画布
   * 2. 清空画布
   * 3. 加载主图片
   * 4. 调用drawImageToCanvas绘制（支持白墨）
   * 5. 返回绘制结果
   *
   * 上下文切换：
   * 为了支持在不同画布上绘制，临时替换this.ctx为目标ctx，
   * 绘制完成后恢复原ctx，避免影响后续操作。
   *
   * @param {Object} params - 参数对象
   * @param {Object} params.ctx - Canvas上下文（可选，不提供则创建临时画布）
   * @param {number} params.canvasWidth - 画布宽度
   * @param {number} params.canvasHeight - 画布高度
   * @param {Object} params.drawCanvasInfo - 画布绘制信息
   * @param {number} params.drawCanvasInfo.inCanvasImageWidth - 原图在画布中的宽度
   * @param {number} params.drawCanvasInfo.inCanvasImageHeight - 原图在画布中的高度
   * @param {Object} params.drawCanvasInfo.currStatus - 当前图片状态
   * @param {string} params.imageSrc - 主图片的URL
   *
   * @returns {Promise<Object>} 绘制结果对象
   * @returns {Object} returns.drawParams - 绘制参数
   * @returns {Object} returns.imageInfo - 图片信息
   * @returns {HTMLCanvasElement} returns.canvas - 绘制完成的画布
   * @returns {CanvasRenderingContext2D} returns.ctx - 绘制上下文
   */
  async executeFullDrawFlowWithoutBackground({
    ctx = null,
    canvasWidth,
    canvasHeight,
    drawCanvasInfo,
    imageSrc,
    isRound = false,
    borderRadius = 0
  }) {
    let transformRatio = 1;
    let targetCanvas, targetCtx;

    if (!ctx) {
      // 创建临时画布
      targetCanvas = document.createElement('canvas');
      targetCtx = targetCanvas.getContext('2d');
      targetCanvas.width = canvasWidth;
      targetCanvas.height = canvasHeight;
    } else {
      // 使用传入的上下文
      targetCtx = ctx;
      targetCanvas = ctx.canvas;
    }

    targetCtx.clearRect(0, 0, canvasWidth, canvasHeight);

    // 加载主图片
    const mainImage = await this.loadImage(imageSrc);

    // 执行主图片绘制
    // 临时保存原始ctx，然后替换为targetCtx
    const originalCtx = this.ctx;
    this.ctx = targetCtx;

    const { drawParams, imageInfo } = await this.drawImageToCanvas({
      image: mainImage,
      canvasWidth,
      canvasHeight,
      drawCanvasInfo,
      transformRatio,
      enableDebug: false,
      isClearCanvas: false // 不清空画布，因为我们要在目标画布上绘制
    });

    // 恢复原始ctx
    this.ctx = originalCtx;

    // 应用裁剪效果
    if (isRound) {
      // 圆形裁剪
      this.applyCircularClip(targetCtx, canvasWidth, canvasHeight);
    } else if (borderRadius && borderRadius > 0) {
      // 圆角矩形裁剪
      this.applyRoundedRectClip(
        targetCtx,
        canvasWidth,
        canvasHeight,
        borderRadius
      );
    }

    return {
      drawParams,
      imageInfo,
      canvas: targetCanvas,
      ctx: targetCtx
    };
  }

  /**
   * 执行有背景图片的完整绘制流程（彩窗模式）
   *
   * 场景说明：
   * 彩窗效果 - 在抠图轮廓内填充背景图案，轮廓外不显示背景。
   * 最终效果：背景图只在轮廓内可见，形成窗口效果。
   *
   * 绘制流程（两次绘制）：
   * 第一次绘制 - 准备轮廓路径：
   * 1. 在临时画布上绘制主图，获取轮廓的ImageData
   * 2. 使用边界跟踪算法提取精确的轮廓路径
   * 3. 将轮廓路径设置为裁剪区域
   *
   * 4. 在裁剪区域内绘制背景图（只有轮廓内的背景可见）
   *
   * 第二次绘制 - 绘制主图：
   * 5. 在背景图上方绘制主图（轮廓图）
   *
   * 最终效果：
   * - 轮廓内：背景图 + 主图叠加
   * - 轮廓外：透明
   *
   * 关键技术：
   * - 使用Path2D或手动路径进行精确裁剪
   * - 边界跟踪算法提取轮廓
   * - 两次绘制实现复杂的合成效果
   *
   * @param {Object} params - 参数对象
   * @param {Object} params.ctx - Canvas上下文（可选，不提供则创建临时画布）
   * @param {number} params.canvasWidth - 画布宽度
   * @param {number} params.canvasHeight - 画布高度
   * @param {Object} params.drawCanvasInfo - 画布绘制信息
   * @param {number} params.drawCanvasInfo.inCanvasImageWidth - 原图在画布中的宽度
   * @param {number} params.drawCanvasInfo.inCanvasImageHeight - 原图在画布中的高度
   * @param {Object} params.drawCanvasInfo.currStatus - 当前图片状态
   * @param {string} params.imageSrc - 主图片的URL
   * @param {Object} params.acrylicWindow - 彩窗配置
   * @param {string} params.acrylicWindow.imageUrl - 背景图片的URL
   * @param {number} params.acrylicWindow.canvasWidth - 原画板的宽度
   *
   * @returns {Promise<Object>} 绘制结果对象
   * @returns {Object} returns.drawParams - 绘制参数
   * @returns {Object} returns.imageInfo - 图片信息
   * @returns {HTMLCanvasElement} returns.canvas - 绘制完成的画布
   * @returns {CanvasRenderingContext2D} returns.ctx - 绘制上下文
   */
  async executeFullDrawFlowWithBackground({
    ctx = null,
    canvasWidth,
    canvasHeight,
    drawCanvasInfo,
    imageSrc,
    acrylicWindow,
    fillBackground,
    isRound = false,
    borderRadius = 0
  }) {
    let transformRatio = 1;
    let targetCanvas, targetCtx;
    if (!ctx) {
      targetCanvas = document.createElement('canvas');
      targetCtx = targetCanvas.getContext('2d');
      targetCanvas.width = canvasWidth;
      targetCanvas.height = canvasHeight;
    } else {
      targetCtx = ctx;
      targetCanvas = ctx.canvas;
    }

    targetCtx.clearRect(0, 0, canvasWidth, canvasHeight);

    const mainImage = await this.loadImage(imageSrc);
    window._debugLog({
      title: '测试',
      'acrylicWindow.imageUrl': acrylicWindow?.imageUrl,
      imageSrc: imageSrc
    });

    // 先绘制主图片到临时画布，获取其轮廓信息
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = canvasWidth;
    tempCanvas.height = canvasHeight;

    // 在临时画布上绘制主图片
    const {
      inCanvasImageWidth,
      inCanvasImageHeight,
      currStatus
    } = drawCanvasInfo;
    const outlineImageRatio = mainImage.width / mainImage.height;
    const originImageRatio = inCanvasImageWidth / inCanvasImageHeight;

    const drawParams = this.calculateDrawParams({
      outlineImageRatio,
      originImageRatio,
      inCanvasImageWidth,
      inCanvasImageHeight,
      currStatus,
      transformRatio
    });

    // 绘制主图片到临时画布
    tempCtx.drawImage(
      mainImage,
      drawParams.dX,
      drawParams.dY,
      drawParams.dWidth,
      drawParams.dHeight
    );

    // 获取主图片的轮廓数据
    const imageData = tempCtx.getImageData(0, 0, canvasWidth, canvasHeight);
    const originalCtx = this.ctx;

    // 绘制背景图片（只在主图片轮廓区域内显示）
    if (acrylicWindow && acrylicWindow.imageUrl) {
      try {
        if (!fillBackground) {
          // 创建精确的轮廓路径
          const outlinePath = this.createPreciseOutlinePath(
            imageData,
            canvasWidth,
            canvasHeight
          );

          const originalCtx = this.ctx;
          this.ctx = targetCtx;

          // 设置裁剪区域为主图片的精确轮廓
          targetCtx.save();

          if (this.supportsPath2D && outlinePath instanceof Path2D) {
            // 使用Path2D裁剪
            targetCtx.clip(outlinePath);
          } else if (outlinePath && outlinePath.length > 0) {
            // 手动绘制轮廓路径
            targetCtx.beginPath();
            targetCtx.moveTo(outlinePath[0].x, outlinePath[0].y);
            for (let i = 1; i < outlinePath.length; i++) {
              targetCtx.lineTo(outlinePath[i].x, outlinePath[i].y);
            }
            targetCtx.closePath();
            targetCtx.clip();
          }
        }

        const { success, error, backgroundInfo } = await this.drawBackground({
          win: acrylicWindow,
          canvasScale: canvasWidth / acrylicWindow.canvasWidth,
          clipImage: null,
          drawCanvasInfo,
          transformRatio
        });

        // 图片: 仅轮廓有背景的图片
        // downloadFile(this.ctx.canvas.toDataURL('image/png'));

        targetCtx.restore();
        this.ctx = originalCtx;

        if (!success) {
          console.warn('背景绘制失败:', error);
        }
      } catch (error) {
        console.error('绘制背景时发生错误:', error);
      }
    }

    // 绘制主图片
    this.ctx = targetCtx;
    const {
      drawParams: newDrawParams,
      imageInfo: newImageInfo
    } = await this.drawImageToCanvas({
      image: mainImage,
      canvasWidth,
      canvasHeight,
      drawCanvasInfo,
      transformRatio,
      enableDebug: false,
      isClearCanvas: false
    });
    this.ctx = originalCtx;

    // 应用裁剪效果
    if (isRound) {
      // 圆形裁剪
      this.applyCircularClip(targetCtx, canvasWidth, canvasHeight);
    } else if (borderRadius && borderRadius > 0) {
      // 圆角矩形裁剪
      this.applyRoundedRectClip(
        targetCtx,
        canvasWidth,
        canvasHeight,
        borderRadius
      );
    }

    return {
      drawParams: newDrawParams,
      imageInfo: newImageInfo,
      canvas: targetCanvas,
      ctx: targetCtx
    };
  }

  /**
   * 创建精确的轮廓路径（兼容性版本）
   *
   * 场景说明：
   * 从图像数据中提取轮廓路径，用于Canvas裁剪操作。
   * 自动检测浏览器是否支持Path2D API，选择最佳实现方式。
   *
   * 兼容性处理：
   * - 支持Path2D：返回Path2D对象（性能更好，现代浏览器）
   * - 不支持Path2D：返回点数组（兼容旧浏览器，需手动绘制路径）
   *
   * 使用场景：
   * 彩窗功能中，将轮廓路径设置为ctx.clip()的裁剪区域。
   *
   * @param {ImageData} imageData - 图像数据对象
   * @param {number} width - 图像宽度
   * @param {number} height - 图像高度
   * @returns {Path2D|Array<{x,y}>|null} Path2D对象、点数组或null
   */
  createPreciseOutlinePath(imageData, width, height) {
    try {
      if (this.supportsPath2D) {
        return this.createPath2DOutline(imageData, width, height);
      } else {
        return this.createFallbackOutline(imageData, width, height);
      }
    } catch (error) {
      console.warn('创建轮廓路径失败，使用备用方案:', error);
      return this.createFallbackOutline(imageData, width, height);
    }
  }

  /**
   * 使用Path2D创建轮廓路径（现代浏览器版本）
   *
   * 功能说明：
   * 使用Path2D API创建轮廓路径，性能更好且使用更方便。
   *
   * 工作流程：
   * 1. 调用findBoundaryPoints提取边界点数组
   * 2. 将边界点添加到Path2D对象
   * 3. 闭合路径
   * 4. 返回Path2D对象，可直接用于ctx.clip()
   *
   * @param {ImageData} imageData - 图像数据对象
   * @param {number} width - 图像宽度
   * @param {number} height - 图像高度
   * @returns {Path2D} 外轮廓路径对象
   */
  createPath2DOutline(imageData, width, height) {
    const data = imageData.data;
    const path = new Path2D();

    // 查找边界点
    const points = this.findBoundaryPoints(data, width, height);

    if (points.length === 0) {
      return path; // 没有找到边界点，返回空路径
    }

    // 构建路径
    for (let i = 0; i < points.length; i++) {
      if (i === 0) {
        path.moveTo(points[i].x, points[i].y);
      } else {
        path.lineTo(points[i].x, points[i].y);
      }
    }

    // 闭合路径
    path.closePath();

    return path;
  }

  /**
   * 备用轮廓创建方案（兼容旧浏览器）
   *
   * 功能说明：
   * 为不支持Path2D的旧浏览器提供降级方案。
   * 返回边界点数组，调用方需手动绘制路径。
   *
   * 工作流程：
   * 1. 调用findBoundaryPoints提取边界点数组
   * 2. 直接返回点数组
   * 3. 调用方需要手动执行：
   *    ctx.beginPath()
   *    ctx.moveTo(points[0].x, points[0].y)
   *    for循环 ctx.lineTo(points[i].x, points[i].y)
   *    ctx.closePath()
   *    ctx.clip()
   *
   * @param {ImageData} imageData - 图像数据对象
   * @param {number} width - 图像宽度
   * @param {number} height - 图像高度
   * @returns {Array<{x,y}>|null} 边界点数组或null（表示没有轮廓）
   */
  createFallbackOutline(imageData, width, height) {
    const data = imageData.data;
    const points = this.findBoundaryPoints(data, width, height);

    if (points.length === 0) {
      return null; // 返回null表示没有轮廓
    }

    return points; // 返回点数组，后续手动绘制
  }

  /**
   * 查找图像的边界点（轮廓跟踪算法）
   *
   * 算法说明：
   * 使用Moore邻域跟踪算法，沿着图像边界顺时针提取轮廓点。
   *
   * 工作原理：
   * 1. 扫描找到第一个非透明像素作为起点
   * 2. 从起点开始，按8方向（右、右下、下、左下、左、左上、上、右上）查找下一个边界点
   * 3. 每找到一个点，记录坐标并继续查找
   * 4. 直到回到起点，形成闭合轮廓
   *
   * 8方向索引：
   * 7  0  1
   * 6  ·  2
   * 5  4  3
   *
   * 应用场景：
   * 用于彩窗功能，提取轮廓后设置为裁剪路径，实现精确的轮廓内绘制。
   *
   * @param {Uint8ClampedArray} data - 图像像素数据（RGBA格式）
   * @param {number} width - 图像宽度
   * @param {number} height - 图像高度
   * @returns {Array<{x: number, y: number}>} 边界点数组，按顺时针顺序排列
   */
  findBoundaryPoints(data, width, height) {
    const points = [];

    try {
      // 首先找到第一个非透明像素
      let startX = -1;
      let startY = -1;

      outer: for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const alpha = data[(y * width + x) * 4 + 3];
          if (alpha > 0) {
            startX = x;
            startY = y;
            break outer;
          }
        }
      }

      if (startX === -1) {
        return points; // 没有非透明像素
      }

      // 使用边界跟踪算法找到轮廓
      const dirs = [
        { dx: 1, dy: 0 }, // 右
        { dx: 1, dy: 1 }, // 右下
        { dx: 0, dy: 1 }, // 下
        { dx: -1, dy: 1 }, // 左下
        { dx: -1, dy: 0 }, // 左
        { dx: -1, dy: -1 }, // 左上
        { dx: 0, dy: -1 }, // 上
        { dx: 1, dy: -1 } // 右上
      ];

      let x = startX;
      let y = startY;
      let dir = 0; // 起始方向
      const visited = new Set();

      do {
        // 记录当前点
        points.push({ x, y });
        visited.add(`${x},${y}`);

        // 尝试从当前方向开始寻找下一个边界点
        let found = false;
        for (let i = 0; i < 8; i++) {
          const testDir = (dir + 6) % 8; // 从左侧开始检查
          const testX = x + dirs[testDir].dx;
          const testY = y + dirs[testDir].dy;

          if (testX >= 0 && testX < width && testY >= 0 && testY < height) {
            const alpha = data[(testY * width + testX) * 4 + 3];
            if (alpha > 0) {
              x = testX;
              y = testY;
              dir = testDir;
              found = true;
              break;
            }
          }

          dir = (dir + 1) % 8;
        }

        if (!found) {
          break; // 没有找到下一个点，退出循环
        }
      } while (x !== startX || y !== startY);
    } catch (error) {
      console.warn('查找边界点失败:', error);
    }

    console.log('边界点数组:', points.length, width, height);
    return points;
  }

  /**
   * 应用圆形裁剪到画布
   *
   * 功能说明：
   * 在画布上应用圆形裁剪，半径为 canvasWidth/2，圆心为画布中心
   * 使用 globalCompositeOperation 的 'destination-in' 模式实现裁剪效果
   *
   * @param {CanvasRenderingContext2D} ctx - Canvas 2D上下文
   * @param {number} canvasWidth - 画布宽度
   * @param {number} canvasHeight - 画布高度
   */
  applyCircularClip(ctx, canvasWidth, canvasHeight) {
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;
    const radius = canvasWidth / 2;

    // 保存当前状态
    ctx.save();

    // 创建圆形路径
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.closePath();

    // 使用 destination-in 模式进行裁剪
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fill();

    // 恢复状态
    ctx.restore();
  }

  /**
   * 应用圆角矩形裁剪到画布
   *
   * 功能说明：
   * 在画布上应用圆角矩形裁剪，类似 CSS border-radius 效果
   * 使用 arcTo 绘制真正的圆弧，使用 globalCompositeOperation 的 'destination-in' 模式实现裁剪效果
   *
   * @param {CanvasRenderingContext2D} ctx - Canvas 2D上下文
   * @param {number} canvasWidth - 画布宽度
   * @param {number} canvasHeight - 画布高度
   * @param {number} borderRadius - 圆角半径
   */
  applyRoundedRectClip(ctx, canvasWidth, canvasHeight, borderRadius) {
    let r = borderRadius;

    // 限制圆角半径不超过画布尺寸的一半
    if (r < 0) {
      r = 0;
    }
    const maxR = Math.min(canvasWidth, canvasHeight) / 2;
    if (r > maxR) {
      r = maxR;
    }

    // 保存当前状态
    ctx.save();

    // 创建圆角矩形路径
    ctx.beginPath();
    if (r === 0) {
      // 如果圆角为0，直接画矩形
      ctx.rect(0, 0, canvasWidth, canvasHeight);
    } else {
      // 使用 arcTo 画真正的圆角矩形（类似 CSS border-radius）
      ctx.moveTo(r, 0);
      ctx.arcTo(canvasWidth, 0, canvasWidth, canvasHeight, r);
      ctx.arcTo(canvasWidth, canvasHeight, 0, canvasHeight, r);
      ctx.arcTo(0, canvasHeight, 0, 0, r);
      ctx.arcTo(0, 0, canvasWidth, 0, r);
    }
    ctx.closePath();

    // 使用 destination-in 模式进行裁剪
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fill();

    // 恢复状态
    ctx.restore();
  }
}

/**
 * 创建CanvasDrawer实例的工厂函数
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D上下文
 * @returns {CanvasDrawer} CanvasDrawer实例
 */
export function createCanvasDrawer(ctx) {
  return new CanvasDrawer(ctx);
}

/**
 * 将base64字符串转换为blobUrl
 * @param {string} base64Data - base64数据URL字符串
 * @returns {string} blobUrl
 */
function base64ToBlobUrl(base64Data) {
  // 检查并分离 base64 数据前缀
  const parts = base64Data.split(',');
  const contentType = parts[0].match(/:(.*?);/)[1]; // 提取 MIME 类型
  const rawBase64 = parts[1]; // 获取真正的 base64 数据

  // 解码 base64 数据为 Blob
  const byteCharacters = atob(rawBase64);
  const byteNumbers = new Array(byteCharacters.length)
    .fill(null)
    .map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: contentType });

  // 创建 Blob URL
  return URL.createObjectURL(blob);
}

/**
 * 将 canvas 导出为 blob URL
 * @param {HTMLCanvasElement} canvas - 画布元素
 * @param {string} [type='image/png'] - MIME 类型
 * @returns {Promise<string>} blob URL
 */
export function canvasToBlobUrl(canvas, type) {
  const mimeType = type || 'image/png';
  return new Promise((resolve, reject) => {
    if (typeof canvas.toBlob === 'function') {
      canvas.toBlob(
        function (blob) {
          let url;
          if (blob) {
            url = URL.createObjectURL(blob);
          } else {
            url = base64ToBlobUrl(canvas.toDataURL(mimeType));
          }
          resolve(url);
        },
        mimeType,
        1.0
      );
    } else {
      try {
        resolve(base64ToBlobUrl(canvas.toDataURL(mimeType)));
      } catch (err) {
        reject(err);
      }
    }
  });
}

/**
 * 灰度转透明度：基于灰度值和原始透明度计算最终透明度（白色透明，黑色不透明）
 * 用于背景图层绘制时的效果处理。
 *
 * @param {string} imageUrl - 源图片 URL
 * @returns {Promise<string>} 处理后的图片 blob URL
 */
export async function convertGrayToAlphaForBackground(imageUrl) {
  const cacheKey = imageUrl;
  const cachedResult = convertGrayToAlphaForBackgroundInstance.get(cacheKey);
  if (cachedResult) {
    return cachedResult;
  }

  const img = await loadImage(imageUrl);
  const width = img.width;
  const height = img.height;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);

  const originalImageData = ctx.getImageData(0, 0, width, height);
  const resultImageData = applyGrayAlphaToImageData(originalImageData);
  ctx.putImageData(resultImageData, 0, 0);

  const result = await canvasToBlobUrl(canvas, 'image/png');
  convertGrayToAlphaForBackgroundInstance.set(cacheKey, result);
  return result;
}

/**
 * 判断图片是否只存在黑白两种像素，如果是则将白色像素变为透明
 *
 * @param {string} imageUrl - 图片 URL 或 base64 字符串
 * @returns {Promise<string>} 返回处理后的图片 blobUrl，如果图片不是黑白则返回原图 blobUrl
 *
 * @description
 * 处理流程：
 * 1. 加载图片到 Canvas
 * 2. 获取像素数据，判断是否只存在黑白两种颜色（使用阈值判断）
 * 3. 如果只有黑白：将白色像素的 alpha 通道设为 0（透明）
 * 4. 如果存在其他颜色：返回原图
 *
 * 阈值判断标准：
 * - 白色像素：RGB 三个通道值都 >= 230
 * - 黑色像素：RGB 三个通道值都 <= 25
 * - 其他颜色：不满足上述两种条件的像素
 */
export function convertWhiteToTransparent(imageUrl) {
  return new Promise((resolve, reject) => {
    // 创建Image对象加载图片
    const img = new Image();
    img.crossOrigin = 'Anonymous'; // 处理跨域问题

    img.onload = function () {
      try {
        // 创建Canvas元素
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');

        // 绘制图片到Canvas
        ctx.drawImage(img, 0, 0);

        // 获取像素数据
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // 阈值定义
        const WHITE_THRESHOLD = 230; // 白色阈值
        const BLACK_THRESHOLD = 25; // 黑色阈值

        function nearWhiteOrBlack(r, g, b) {
          // 保护一下输入（避免传入小数/字符串）
          r = Number(r);
          g = Number(g);
          b = Number(b);

          // 到黑色(0,0,0)的平方距离
          var dBlack = r * r + g * g + b * b;

          // 到白色(255,255,255)的平方距离
          var dr = 255 - r;
          var dg = 255 - g;
          var db = 255 - b;
          var dWhite = dr * dr + dg * dg + db * db;

          return dWhite <= dBlack;
        }

        // 遍历像素，判断是否只有黑白两种颜色
        let hasWhite = false;
        let hasBlack = false;
        let hasOtherColor = false;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];

          // 跳过透明像素
          if (a === 0) {
            continue;
          }
        }

        // 如果只有黑白两种颜色，将白色像素变为透明

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          // 如果是白色像素，将 alpha 设为 0（透明）
          if (nearWhiteOrBlack(r, g, b)) {
            data[i + 3] = 0;
          }
        }

        // 将处理后的像素数据写回Canvas
        ctx.putImageData(imageData, 0, 0);

        // 将Canvas转为blobUrl
        canvasToBlobUrl(canvas, 'image/png').then(resolve).catch(reject);
      } catch (error) {
        console.error('convertWhiteToTransparent:处理图片失败', error);
        reject(new Error('处理图片失败'));
      }
    };

    img.onerror = function () {
      reject(new Error('图片加载失败'));
      console.error(
        'convertWhiteToTransparent:图片加载失败,imageUrl=' + imageUrl
      );
    };

    img.src = imageUrl;
  });
}

/**
 * 将灰度图的灰度值转换为透明度并应用到目标图像数据
 *
 * @param {ImageData} targetImageData - 目标图像数据（原图）
 * @param {ImageData} [whiteInkImageData] - 白墨图的图像数据（可选），如果提供，只有当对应位置为透明像素时才执行透明度计算
 * @returns {ImageData} 返回处理后的目标图像数据（RGB 不变，alpha 根据灰度值计算）
 *
 * @description
 * 处理逻辑：
 * 1. 如果 whiteInkImageData 存在，检查对应位置的 alpha 值，只有当 alpha <= 10（透明像素）时才执行后续步骤
 * 2. 根据灰度值计算透明度：alpha = 255 - grayValue（灰度值越高，透明度越低）
 * 3. 将计算出的透明度应用到目标图像的 alpha 通道
 * 4. 保持目标图像的 RGB 值不变
 *
 * 参考：灰度转透明度工具 - 修复灰度计算逻辑.html
 */
export function applyGrayAlphaToImageData(targetImageData, whiteInkImageData) {
  // 如果 whiteInkImageData 存在，验证其尺寸是否一致
  if (whiteInkImageData) {
    if (
      whiteInkImageData.width !== targetImageData.width ||
      whiteInkImageData.height !== targetImageData.height
    ) {
      throw new Error(
        '白墨图和目标图像的尺寸不一致：' +
          `whiteInkImageData(${whiteInkImageData.width}x${whiteInkImageData.height}) vs ` +
          `targetImageData(${targetImageData.width}x${targetImageData.height})`
      );
    }
  }

  const targetData = targetImageData.data;
  const whiteInkData = whiteInkImageData ? whiteInkImageData.data : null;

  // 遍历每个像素（每 4 个元素为一组：R, G, B, A）
  for (let i = 0; i < targetData.length; i += 4) {
    const r = targetData[i];
    const g = targetData[i + 1];
    const b = targetData[i + 2];
    const a = targetData[i + 3];

    // 1 转线性空间（关键）
    let rLin = 4 * Math.pow(r / 255 - 0.5, 3) + 0.5;
    let gLin = 4 * Math.pow(g / 255 - 0.5, 3) + 0.5;
    let bLin = 4 * Math.pow(b / 255 - 0.5, 3) + 0.5;

    //  计算亮度（物理亮度）
    let luminance = 0.299 * rLin + 0.587 * gLin + 0.114 * bLin;

    if (whiteInkData) {
      //如果存在白墨
      const whiteAlpha = whiteInkData[i + 3];
      // customAlpha ==0 表示完全透明 对应灰度值应该越大 无白墨
      let whiteLin = Math.pow(1 - whiteAlpha / 255, 1 / 3);
      luminance = whiteLin * luminance;
    }

    // 4️⃣ 白墨量（反转）
    let whiteInk = 1 - luminance;

    // 5️⃣ 转回 0~255
    let alpha = whiteInk * a;

    targetData[i + 3] = Math.min(255, Math.max(0, Math.round(alpha)));
  }

  // 返回处理后的 targetImageData（直接修改并返回）
  return targetImageData;
}

/**
 * 使用灰度图透明度处理图片并导出 blobUrl
 *
 * @param {Object} params - 参数对象
 * @param {CanvasRenderingContext2D} [params.ctx] - 画布上下文，可选
 * @param {string} [params.whiteInkImageSrc] - 白墨图片路径（可选），如果提供，只有当对应位置为透明像素时才执行透明度计算
 * @param {string} params.outlineImageSrc - 原图路径
 * @param {number} params.canvasWidth - 画布宽度
 * @param {number} params.canvasHeight - 画布高度
 * @param {number} [params.opacity] - 透明度，范围 0-1，默认为 1（用于兼容参数，实际不使用）
 * @param {boolean} [params.exportImage] - 是否导出图片，默认 false（和 drawImageWithMask 保持一致）
 * @returns {Promise<string|undefined>} exportImage=true 时返回处理后的图片 blobUrl，false 时不返回
 *
 * @description
 * 处理流程：
 * 1. 创建 canvas（如果没有传入 ctx）
 * 2. 加载原图
 * 3. 计算缩放参数和居中位置（和 drawImageWithMask 保持一致）
 * 4. 生成灰度图：convertToGrayImageCache(outlineImageSrc)
 * 5. 加载灰度图
 * 6. 创建临时 canvas，绘制灰度图和原图
 * 7. 获取灰度图和原图的 imageData
 * 8. 如果 whiteInkImageSrc 存在，加载并获取 whiteInk 的 imageData
 * 9. 使用 applyGrayAlphaToImageData 将灰度图的透明度应用到原图（如果 whiteInkImageSrc 存在，只有当对应位置为透明像素时才执行）
 * 10. 将处理后的 imageData 绘制到主 canvas
 * 11. 导出 blobUrl（仅在 exportImage=true 时）
 * 12. 清理临时资源
 */
export async function drawImageWithGrayAlpha({
  ctx,
  whiteInkImageSrc,
  outlineImageSrc,
  canvasWidth,
  canvasHeight,
  opacity = 1,
  exportImage = false
}) {
  let canvasNode;
  if (!ctx) {
    const { ctx: newCtx, canvasNode: newCanvasNode } = await initCanvas({
      dom: document.createElement('canvas'),
      canvasWidth,
      canvasHeight
    });
    ctx = newCtx;
    canvasNode = newCanvasNode;
  }

  // 加载原图
  const outlineImg = await loadImage(outlineImageSrc);

  // 计算目标图片的缩放参数和居中位置（和 drawImageWithMask 保持一致）
  const targetScale = Math.min(
    canvasWidth / outlineImg.width,
    canvasHeight / outlineImg.height
  );

  const targetWidth = outlineImg.width * targetScale;
  const targetHeight = outlineImg.height * targetScale;
  const targetX = (canvasWidth - targetWidth) / 2;
  const targetY = (canvasHeight - targetHeight) / 2;

  // 获取设备像素比
  const dpr = window.devicePixelRatio || 1;
  const actualWidth = Math.floor(canvasWidth * dpr);
  const actualHeight = Math.floor(canvasHeight * dpr);

  // 创建临时 canvas 用于绘制灰度图
  const grayCanvas = document.createElement('canvas');
  const { ctx: grayCtx, canvasNode: grayCanvasNode } = await initCanvas({
    dom: grayCanvas,
    canvasWidth,
    canvasHeight
  });

  // 创建临时 canvas 用于绘制原图
  const targetCanvas = document.createElement('canvas');
  const { ctx: targetCtx, canvasNode: targetCanvasNode } = await initCanvas({
    dom: targetCanvas,
    canvasWidth,
    canvasHeight
  });

  // 绘制原图到临时 canvas（使用和 drawImageWithMask 相同的坐标和尺寸）
  targetCtx.drawImage(outlineImg, targetX, targetY, targetWidth, targetHeight);

  // 获取原图的图像数据
  const targetImageData = targetCtx.getImageData(
    0,
    0,
    actualWidth,
    actualHeight
  );

  // 如果 whiteInkImageSrc 存在，获取 whiteInk 的图像数据
  let whiteInkImageData = null;
  if (whiteInkImageSrc) {
    const whiteInkImg = await loadImage(whiteInkImageSrc);

    // 创建临时 canvas 用于绘制 whiteInk
    const whiteInkCanvas = document.createElement('canvas');
    const {
      ctx: whiteInkCtx,
      canvasNode: whiteInkCanvasNode
    } = await initCanvas({
      dom: whiteInkCanvas,
      canvasWidth,
      canvasHeight
    });

    // 绘制 whiteInk 到临时 canvas（使用和 drawImageWithMask 相同的坐标和尺寸）
    whiteInkCtx.drawImage(
      whiteInkImg,
      targetX,
      targetY,
      targetWidth,
      targetHeight
    );

    // 获取 whiteInk 的图像数据
    whiteInkImageData = whiteInkCtx.getImageData(
      0,
      0,
      actualWidth,
      actualHeight
    );

    // 清理 whiteInk 临时资源
    whiteInkCanvasNode.width = 0;
    whiteInkCanvasNode.height = 0;
  }

  window._debugLog({
    title: 'drawImageWithGrayAlpha',
    原图: outlineImageSrc,
    白墨图: whiteInkImageSrc
  });

  // 使用 applyGrayAlphaToImageData 将灰度图的透明度应用到原图
  const resultImageData = applyGrayAlphaToImageData(
    targetImageData, // outlineImageSrc绘制
    whiteInkImageData
  );

  // 将处理后的图像数据绘制到主 canvas
  ctx.putImageData(resultImageData, 0, 0);

  // 清理临时资源
  grayCanvasNode.width = 0;
  grayCanvasNode.height = 0;
  targetCanvasNode.width = 0;
  targetCanvasNode.height = 0;

  // 导出 blobUrl（仅在 exportImage=true 时）
  if (exportImage) {
    return await canvasToBlobUrl(canvasNode, 'image/png');
  }
}

/**
 * 判断图片是否只存在黑白两种像素，如果是则将白色像素变为透明
 *
 * @param {string} imageUrl - 图片 URL 或 base64 字符串
 * @returns {Promise<string>} 返回处理后的图片 blobUrl，如果图片不是黑白则返回原图 blobUrl
 *
 * @description
 * 处理流程：
 * 1. 加载图片到 Canvas
 * 2. 获取像素数据，判断是否只存在黑白两种颜色（使用阈值判断）
 * 3. 如果只有黑白：将白色像素的 alpha 通道设为 0（透明）
 * 4. 如果存在其他颜色：返回原图
 *
 * 阈值判断标准：
 * - 白色像素：RGB 三个通道值都 >= 230
 * - 黑色像素：RGB 三个通道值都 <= 25
 * - 其他颜色：不满足上述两种条件的像素
 */
function convertBackendWhiteInkToFrontendWhiteInk(imageUrl) {
  return new Promise((resolve, reject) => {
    // 创建Image对象加载图片
    const img = new Image();
    img.crossOrigin = 'Anonymous'; // 处理跨域问题

    img.onload = function () {
      try {
        // 创建Canvas元素
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');

        // 绘制图片到Canvas
        ctx.drawImage(img, 0, 0);

        // 获取像素数据
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        console.log('imageData', imageData);

        const data = imageData.data;

        // 阈值定义
        const WHITE_THRESHOLD = 240; // 白色阈值
        const BLACK_THRESHOLD = 25; // 黑色阈值

        // 判断是否为白色像素
        function isWhitePixel(r, g, b) {
          return (
            r >= WHITE_THRESHOLD && g >= WHITE_THRESHOLD && b >= WHITE_THRESHOLD
          );
        }

        // 判断是否为黑色像素
        function isBlackPixel(r, g, b) {
          return (
            r <= BLACK_THRESHOLD && g <= BLACK_THRESHOLD && b <= BLACK_THRESHOLD
          );
        }

        // 遍历像素，判断是否只有黑白两种颜色
        let hasWhite = true;
        let hasBlack = true;
        let hasOtherColor = false;

        // 如果存在其他颜色，直接返回原图
        if (hasOtherColor) {
          canvasToBlobUrl(canvas, 'image/png').then(resolve).catch(reject);
          return;
        }

        // 如果只有黑白两种颜色，将白色像素变为透明
        if (hasWhite && hasBlack) {
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            if (isBlackPixel(r, g, b)) {
              data[i + 3] = 0;
            }
          }

          // 将处理后的像素数据写回Canvas
          ctx.putImageData(imageData, 0, 0);
        }

        // 将Canvas转为blobUrl
        canvasToBlobUrl(canvas, 'image/png').then(resolve).catch(reject);
      } catch (error) {
        console.error('convertWhiteToTransparent:处理图片失败', error);
        reject(new Error('处理图片失败'));
      }
    };

    img.onerror = function () {
      reject(new Error('图片加载失败'));
      console.error(
        'convertWhiteToTransparent:图片加载失败,imageUrl=' + imageUrl
      );
    };

    img.src = imageUrl;
  });
}

/**
 * 从两点和 bulge 值计算圆弧参数
 * @param {number} x1 - 起点 X 坐标
 * @param {number} y1 - 起点 Y 坐标
 * @param {number} x2 - 终点 X 坐标
 * @param {number} y2 - 终点 Y 坐标
 * @param {number} bulge - bulge 值，表示圆弧的弯曲程度
 * @returns {Object|null} 返回圆弧参数对象 {cx, cy, radius, startAngle, endAngle, ccw}，如果 bulge 为 0 则返回 null
 */
function getArcData(x1, y1, x2, y2, bulge) {
  if (Math.abs(bulge) < 1e-6) return null;

  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.hypot(dx, dy);
  if (dist === 0) return null;

  const chord = dist / 2;
  const radius = (chord * (1 + bulge * bulge)) / (2 * Math.abs(bulge));

  // 弦中点
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;

  // 垂直方向单位向量（左手法则：bulge>0 时向上偏）
  const perpX = -dy / dist;
  const perpY = dx / dist;

  // 圆心偏移距离
  const offset = radius - chord * Math.abs(bulge);
  const cx = mx + perpX * (bulge > 0 ? offset : -offset);
  const cy = my + perpY * (bulge > 0 ? offset : -offset);

  let startAngle = Math.atan2(y1 - cy, x1 - cx);
  let endAngle = Math.atan2(y2 - cy, x2 - cx);

  // 角度调整（确保 sweep 正确）
  if (bulge > 0) {
    if (endAngle <= startAngle) endAngle += 2 * Math.PI;
  } else {
    if (endAngle >= startAngle) endAngle -= 2 * Math.PI;
  }

  return { cx, cy, radius, startAngle, endAngle, ccw: bulge > 0 };
}

/**
 * 绘制一条闭合多段线（支持 bulge）
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D 上下文
 * @param {Array} points - 点数组，每个点为 [x, y, bulge]
 * @param {number} scaleX - X 轴缩放比例，默认为 1
 * @param {number} scaleY - Y 轴缩放比例，默认为 -1（Canvas Y 向下，DXF Y 向上）
 * @param {number} offsetX - X 轴偏移量，默认为 0
 * @param {number} offsetY - Y 轴偏移量，默认为 0
 */
function drawClosedPolyline(ctx, points, scaleX, scaleY, offsetX, offsetY) {
  // ctx.strokeStyle = '#d32f2f';
  // ctx.lineWidth = 1.5;
  if (scaleX === undefined) scaleX = 1;
  if (scaleY === undefined) scaleY = -1;
  if (offsetX === undefined) offsetX = 0;
  if (offsetY === undefined) offsetY = 0;

  const n = points.length;
  if (n < 2) return;

  ctx.beginPath();

  // 起始点
  const x0 = points[0][0];
  const y0 = points[0][1];
  ctx.moveTo(x0 * scaleX + offsetX, y0 * scaleY + offsetY);

  // 遍历每一段：points[i] -> points[(i+1)%n]
  for (let i = 0; i < n; i++) {
    const point1 = points[i];
    const point2 = points[(i + 1) % n];
    const x1 = point1[0];
    const y1 = point1[1];
    const bulge = point1[2] || 0;
    const x2 = point2[0];
    const y2 = point2[1];

    const X1 = x1 * scaleX + offsetX;
    const Y1 = y1 * scaleY + offsetY;
    const X2 = x2 * scaleX + offsetX;
    const Y2 = y2 * scaleY + offsetY;

    if (Math.abs(bulge) < 1e-6) {
      ctx.lineTo(X2, Y2);
    } else {
      const arc = getArcData(x1, y1, x2, y2, bulge);
      if (arc) {
        const cx = arc.cx * scaleX + offsetX;
        const cy = arc.cy * scaleY + offsetY;
        const r = arc.radius * Math.abs(scaleX); // 假设等比缩放
        ctx.arc(cx, cy, r, arc.startAngle, arc.endAngle, !arc.ccw);
      } else {
        ctx.lineTo(X2, Y2);
      }
    }
  }

  ctx.closePath(); // 可选，但视觉上更安全
  ctx.stroke();
}

/**
 * 绘制 DXF 刀线
 * @param {Object} params - 参数对象
 * @param {number} params.width - Canvas 宽度（像素）
 * @param {number} params.height - Canvas 高度（像素）
 * @param {CanvasRenderingContext2D} params.ctx - Canvas 2D 上下文
 * @param {Array} params.dxfKnifeLines - DXF 刀线数据数组，每个元素是一条多段线（点数组），每个点为 [x, y, bulge]（单位：毫米）
 */
function drawDxfKnifeLines(params) {
  const width = params.width;
  const height = params.height;
  const ctx = params.ctx;
  const dxfKnifeLines = params.dxfKnifeLines;

  if (!ctx || !dxfKnifeLines || !Array.isArray(dxfKnifeLines)) {
    console.error('drawDxfKnifeLines: 参数错误');
    return;
  }

  // DPI 转换系数：300 DPI，1 英寸 = 25.4 毫米
  // const dpi = 300;
  // const mmToInch = 1 / 25.4;
  // const scale = dpi * mmToInch; // 300 / 25.4

  // 遍历每条刀线
  for (let i = 0; i < dxfKnifeLines.length; i++) {
    const polyline = dxfKnifeLines[i];
    if (!polyline || !Array.isArray(polyline) || polyline.length < 2) {
      continue;
    }

    // 将 DXF 坐标（毫米）转换为像素坐标
    const data = [];
    for (let j = 0; j < polyline.length; j++) {
      const item = polyline[j];
      if (item && Array.isArray(item) && item.length >= 2) {
        const x = (item[0] || 0) * width;
        const y = (item[1] || 0) * height;
        const bulge = item[2] || 0;
        data.push([x, y, bulge]);
      }
    }

    if (data.length < 2) {
      continue;
    }

    // 绘制闭合多段线
    // 注意：Canvas Y 向下，DXF Y 向上 → scaleY = -1
    // Y 轴翻转后需要加上 height 偏移，确保绘制在 Canvas 可见区域内
    drawClosedPolyline(ctx, data, 1, 1, 0, 0);
  }
}

/**
 * 将图片转换为mask格式（按比例重新绘制，有像素变透明，透明变黑色）
 *
 * 功能说明：
 * 1. 输入一张PNG图片和一个比例（如 16/9）
 * 2. 基于图片较长边按比例计算新画布尺寸
 * 3. 将图片绘制到新画布，保持较长边，另一边居中（类似 object-fit: contain）
 * 4. 处理像素：原图有像素部分（alpha > 0）变为透明，透明部分（alpha = 0）变为黑色
 *
 * @param {Object} params - 参数对象
 * @param {string} params.imageSrc - 源图片URL或base64
 * @param {number} params.ratio - 目标比例（宽度/高度），如 16/9
 * @param {number} [params.maxWidth] - 最大宽度限制，默认4096
 * @param {number} [params.maxHeight] - 最大高度限制，默认4096
 * @returns {Promise<string>} 返回处理后的图片 blob URL（PNG 格式）
 */
export async function convertImageToMaskWithRatio({
  imageSrc,
  ratio,
  maxWidth = 4096,
  maxHeight = 4096
}) {
  // 加载原图
  const img = await loadImage(imageSrc);
  const imgWidth = img.width;
  const imgHeight = img.height;

  // 确定图片较长边
  const longerSide = Math.max(imgWidth, imgHeight);

  // 根据比例计算新画布尺寸
  let canvasWidth, canvasHeight;
  if (imgWidth >= imgHeight) {
    // 宽度是较长边
    canvasWidth = longerSide;
    canvasHeight = longerSide / ratio;
  } else {
    // 高度是较长边
    canvasHeight = longerSide;
    canvasWidth = longerSide * ratio;
  }

  // 限制最大尺寸
  if (canvasWidth > maxWidth) {
    const scale = maxWidth / canvasWidth;
    canvasWidth = maxWidth;
    canvasHeight = canvasHeight * scale;
  }
  if (canvasHeight > maxHeight) {
    const scale = maxHeight / canvasHeight;
    canvasHeight = maxHeight;
    canvasWidth = canvasWidth * scale;
  }

  // 确保尺寸为整数
  canvasWidth = Math.floor(canvasWidth);
  canvasHeight = Math.floor(canvasHeight);

  // 创建临时canvas用于绘制原图（获取原图像素数据）
  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = imgWidth;
  tempCanvas.height = imgHeight;
  const tempCtx = tempCanvas.getContext('2d');
  tempCtx.drawImage(img, 0, 0, imgWidth, imgHeight);

  // 获取原图的图像数据
  const originalImageData = tempCtx.getImageData(0, 0, imgWidth, imgHeight);
  const originalData = originalImageData.data;

  // 创建新画布
  const newCanvas = document.createElement('canvas');
  newCanvas.width = canvasWidth;
  newCanvas.height = canvasHeight;
  const newCtx = newCanvas.getContext('2d');

  // 计算图片在新画布中的绘制尺寸和位置（保持较长边，另一边居中）
  const scale = Math.min(canvasWidth / imgWidth, canvasHeight / imgHeight);
  const drawWidth = imgWidth * scale;
  const drawHeight = imgHeight * scale;
  const drawX = (canvasWidth - drawWidth) / 2;
  const drawY = (canvasHeight - drawHeight) / 2;

  // 先绘制原图到新画布（用于确定绘制区域）
  newCtx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

  // 获取新画布的图像数据
  const newImageData = newCtx.getImageData(0, 0, canvasWidth, canvasHeight);
  const newData = newImageData.data;

  // 遍历新画布的所有像素
  for (let i = 0; i < newData.length; i += 4) {
    const pixelIndex = i / 4;
    const x = pixelIndex % canvasWidth;
    const y = Math.floor(pixelIndex / canvasWidth);

    // 判断当前像素是否在图片绘制区域内
    if (
      x >= drawX &&
      x < drawX + drawWidth &&
      y >= drawY &&
      y < drawY + drawHeight
    ) {
      // 计算对应原图的像素坐标
      const srcX = Math.floor((x - drawX) / scale);
      const srcY = Math.floor((y - drawY) / scale);

      // 确保坐标在有效范围内
      if (srcX >= 0 && srcX < imgWidth && srcY >= 0 && srcY < imgHeight) {
        const srcIndex = (srcY * imgWidth + srcX) * 4;
        const srcAlpha = originalData[srcIndex + 3];

        // 根据原图像素透明度处理新画布像素
        if (srcAlpha > 0) {
          // 原图有像素 → 新画布变为透明
          newData[i] = 0; // R
          newData[i + 1] = 0; // G
          newData[i + 2] = 0; // B
          newData[i + 3] = 0; // A (透明)
        } else {
          // 原图透明 → 新画布变为黑色
          newData[i] = 0; // R
          newData[i + 1] = 0; // G
          newData[i + 2] = 0; // B
          newData[i + 3] = 255; // A (不透明黑色)
        }
      } else {
        // 超出原图范围，设置为黑色
        newData[i] = 0;
        newData[i + 1] = 0;
        newData[i + 2] = 0;
        newData[i + 3] = 255;
      }
    } else {
      // 不在图片绘制区域内，设置为黑色
      newData[i] = 0;
      newData[i + 1] = 0;
      newData[i + 2] = 0;
      newData[i + 3] = 255;
    }
  }

  // 将处理后的图像数据绘制到新画布
  newCtx.putImageData(newImageData, 0, 0);

  // 导出结果为 blob URL
  const result = await canvasToBlobUrl(newCanvas, 'image/png');

  // 释放内存
  img.src = '';
  tempCanvas.width = 0;
  tempCanvas.height = 0;
  newCanvas.width = 0;
  newCanvas.height = 0;

  return result;
}

function exportCanvasToBlobUrl({
  myCanvas,
  imageType = 'image/png',
  quality = 1
}) {
  return new Promise((resolve, reject) => {
    myCanvas.toBlob(
      (blob) => {
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          resolve(blobUrl);
        } else {
          reject(new Error('canvas 转换为 blob 失败'));
        }
      },
      imageType,
      quality
    ); // 可以调整格式和质量参数
  });
}

/**
 * 按“白色像素”的最小外接矩形裁剪图片。
 * - 用于黑白 mask：裁剪出白色区域的 bbox（四周非白边会被裁掉）
 * - 白色判定：阈值判断，且透明像素(a=0)也视为白色
 *
 * @param {string} imageUrl - 图片 URL 或 base64(dataUrl)
 * @param {Object} [opt]
 * @param {number} [opt.whiteThreshold=230] - 白色阈值（0~255），越小越容易被认为是白
 * @returns {Promise<string>} 裁剪后的 dataUrl；若未找到白像素则返回原图
 */
export async function cropImageAroundNotWhite(imageUrl, opt) {
  opt = opt || {};
  var whiteThreshold =
    typeof opt.whiteThreshold === 'number' ? opt.whiteThreshold : 230;

  const img = await loadImage(imageUrl);
  const width = img.width;
  const height = img.height;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  var minX = width;
  var minY = height;
  var maxX = -1;
  var maxY = -1;

  function isWhitePixel(r, g, b, a) {
    // 透明像素也算白（按你的口径）
    if (a === 0) return true;
    return r >= whiteThreshold && g >= whiteThreshold && b >= whiteThreshold;
  }

  for (var y = 0; y < height; y++) {
    for (var x = 0; x < width; x++) {
      var idx = (y * width + x) * 4;
      var r = data[idx];
      var g = data[idx + 1];
      var b = data[idx + 2];
      var a = data[idx + 3];
      if (isWhitePixel(r, g, b, a)) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  // 全图未找到白像素：直接返回原图，避免异常
  if (maxX < 0 || maxY < 0) {
    canvas.width = 0;
    canvas.height = 0;
    return imageUrl;
  }

  var cropW = maxX - minX + 1;
  var cropH = maxY - minY + 1;
  if (cropW <= 0 || cropH <= 0) {
    canvas.width = 0;
    canvas.height = 0;
    return imageUrl;
  }

  const exportCanvas = document.createElement('canvas');
  exportCanvas.width = cropW;
  exportCanvas.height = cropH;
  const exportCtx = exportCanvas.getContext('2d');
  exportCtx.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);

  const result = exportCanvas.toDataURL('image/png');

  // 释放内存
  canvas.width = 0;
  canvas.height = 0;
  exportCanvas.width = 0;
  exportCanvas.height = 0;

  return result;
}

/**
 * 将背景图与描边图合成：背景按蒙版裁剪后与 outlineImageSrc 叠加
 * @param {Object} opt
 * @param {string} opt.bgOnlyDataUrl - 仅含背景层的 dataURL
 * @param {string} opt.outlineImageSrc - 描边图 URL
 * @param {string} opt.maskBase64 - imageBorderParseReturnMask base64 字符串
 * @param {Function} [opt.debugLog] - 可选调试日志函数
 * @returns {Promise<string>} 合成后的 blob URL
 */
export async function composeBgWithOutlineImage({
  bgOnlyDataUrl,
  outlineImageSrc,
  maskBase64,
  debugLog,
  noClip = false
}) {
  var log = typeof debugLog === 'function' ? debugLog : function () {};

  if (!maskBase64) {
    throw new Error(
      'imageBorderParseReturnMask 缺失，降级为直接使用 outlineImageSrc'
    );
  }

  // 步骤1：生成蒙版图 A（先按白色像素 bbox 裁剪，再白转透明）
  var maskDataUrl =
    maskBase64.indexOf('blob:') === 0
      ? maskBase64
      : 'data:image/png;base64,' + maskBase64;

  var croppedMaskDataUrl;
  var aMaskUrl;
  if (!noClip) {
    croppedMaskDataUrl = await cropImageAroundNotWhite(maskDataUrl);
    aMaskUrl = await convertWhiteToTransparent(croppedMaskDataUrl);
  } else {
    croppedMaskDataUrl = maskDataUrl;
    aMaskUrl = await convertWhiteToTransparent(croppedMaskDataUrl);
  }

  function loadImgEl(src) {
    return new Promise(function (resolve, reject) {
      var el = new Image();
      el.onload = function () {
        resolve(el);
      };
      el.onerror = reject;
      el.src = src;
    });
  }
  var bgImgEl = await loadImgEl(bgOnlyDataUrl);
  var aImgEl = await loadImgEl(aMaskUrl);
  var outlineImgEl = await loadImgEl(outlineImageSrc);

  // 步骤2：以 aMaskUrl / outlineImageSrc 同尺寸为基准，先生成背景 A
  var bgW = bgImgEl.width;
  var bgH = bgImgEl.height;
  var oW = outlineImgEl.width;
  var oH = outlineImgEl.height;

  // 背景等比缩放填充参数
  var bgDrawW, bgDrawH, bgLeft, bgTop;
  if (oW / oH > bgW / bgH) {
    bgDrawW = oW;
    bgDrawH = Math.round(bgH * (oW / bgW));
    bgLeft = 0;
    bgTop = Math.round((oH - bgDrawH) / 2);
  } else {
    bgDrawH = oH;
    bgDrawW = Math.round(bgW * (oH / bgH));
    bgTop = 0;
    bgLeft = Math.round((oW - bgDrawW) / 2);
  }

  // 创建背景画布 A，绘制缩放后的背景
  var bgCanvasA = document.createElement('canvas');
  bgCanvasA.width = oW;
  bgCanvasA.height = oH;
  var bgCtxA = bgCanvasA.getContext('2d');
  bgCtxA.drawImage(bgImgEl, bgLeft, bgTop, bgDrawW, bgDrawH);

  // 用 aMaskUrl 裁剪背景 A：aMask 透明区域对应的背景保留，不透明区域擦除
  bgCtxA.globalCompositeOperation = 'destination-out';
  bgCtxA.drawImage(aImgEl, 0, 0, oW, oH);
  bgCtxA.globalCompositeOperation = 'source-over';

  // 步骤3：创建最终画布，先绘制背景 A，再叠加 outlineImageSrc
  var finalCanvas = document.createElement('canvas');
  finalCanvas.width = oW;
  finalCanvas.height = oH;
  var finalCtx = finalCanvas.getContext('2d');
  finalCtx.drawImage(bgCanvasA, 0, 0, oW, oH);
  finalCtx.drawImage(outlineImgEl, 0, 0, oW, oH);

  var finalImgSrc = base64ToBlobUrl(finalCanvas.toDataURL('image/png'));

  log({
    title: '描边背景合成:composeBgWithOutlineImage',
    outlineImageSrc,
    bgOnlyDataUrl,
    maskDataUrl,
    croppedMaskDataUrl,
    aMaskUrl,
    finalImgSrc
  });

  return finalImgSrc;
}

export default {
  ImageCreator,
  initCanvas,
  createImageCreator,
  clearImageCache,
  fillNonTransparentPixels,
  fitImageInCanvas,
  drawImageWithMask,
  drawImageWithGrayAlpha,
  loadImage,
  CanvasDrawer,
  createCanvasDrawer,
  convertWhiteToTransparent,
  applyGrayAlphaToImageData,
  convertBackendWhiteInkToFrontendWhiteInk,
  drawDxfKnifeLines,
  convertImageToMaskWithRatio,
  exportCanvasToBlobUrl,
  cropImageAroundNotWhite,
  composeBgWithOutlineImage,
  exportImageWithRatioPadding
};
